import * as THREE from 'three';
import { Rng } from '../../core/rng';
import signPlates from './signPlates.json';
// Imported up front (not on demand): the requests leave the moment the materials are made, so the files download
// while the rest of the city is built instead of after it (docs/PERF.md "Load time").
import { ktx2Loader } from '../../render/ktx2';
import { needForFirstView } from '../../render/shaderWarmup';

/**
 * Canvas-generated textures for Low Borough. Everything is painted procedurally at load time:
 * a facade atlas (+ matching emissive atlas for lit windows), tiling ground textures with wet
 * roughness, a signage atlas, and small utility textures (lamp pool glow, water normals).
 *
 * Palette: sooty brick reds, bottle greens, slate blues, sodium amber.
 */

export const ATLAS_SIZE = 2048;
export const TILE_PX = 256;
const TILES_PER_ROW = ATLAS_SIZE / TILE_PX;

export const T = {
  brickPlain: 0,
  brickWinDark: 1,
  brickWinLit: 2,
  brickWinTV: 3,
  brickDoor: 4,
  brickWinDark2: 5,
  brickArchWinDark: 6,
  brickArchWinLit: 7,
  loadingDoor: 8,
  stuccoPlain: 9,
  stuccoWinDark: 10,
  stuccoWinLit: 11,
  shopWinLit: 12,
  shopWinLit2: 13,
  shopShutter: 14,
  shopDoorLit: 15,
  pubWinLit: 16,
  /** The second awning colourway (navy / cream); `awning` is red / cream. Both are tinted near-white. */
  awningBlue: 17,
  corrugated: 18,
  garageDoor: 19,
  rollerDoor: 20,
  concretePlain: 21,
  flatsWinDark: 22,
  flatsWinLit: 23,
  stonePlain: 24,
  stoneWinDark: 25,
  stoneWinLit: 26,
  lancetLit: 27,
  factoryWinDark: 28,
  factoryWinLit: 29,
  slate: 30,
  felt: 31,
  corrugatedRoof: 32,
  white: 33,
  awning: 34,
  glazingLit: 35,
  glazingDark: 36,
  timber: 37,
  archLockup: 38,
  archWorkshopLit: 39,
  girder: 40,
  brickSoot: 41,
  stoneDoor: 42,
  pubDoor: 43,
  clubFront: 44,
  deckFront: 45,
  stuccoDoor: 46,
  brickWinLit2: 47,
  lead: 48,
  brickGable: 49,
  shopWinDark: 50,
  archBlank: 51,
  // City pass 2: London stock brick, net curtains, boarded windows, shop-type fronts.
  stockPlain: 52,
  stockWinDark: 53,
  stockWinLit: 54,
  stockDoor: 55,
  brickWinNets: 56,
  stockWinNets: 57,
  brickWinBoarded: 58,
  launderette: 59,
  bookieWin: 60,
  cafeWin: 61,
  brickWinDim: 62,
  stockWinLit2: 63,
} as const;
export type TileId = (typeof T)[keyof typeof T];

/** UV rect [u0, v0, u1, v1] of a tile (inset to limit mip bleeding). v is flipped for three.js. */
export function tileUV(id: number, inset = 3): [number, number, number, number] {
  const cx = id % TILES_PER_ROW;
  const cy = Math.floor(id / TILES_PER_ROW);
  const u0 = (cx * TILE_PX + inset) / ATLAS_SIZE;
  const u1 = ((cx + 1) * TILE_PX - inset) / ATLAS_SIZE;
  const vTop = (cy * TILE_PX + inset) / ATLAS_SIZE;
  const vBot = ((cy + 1) * TILE_PX - inset) / ATLAS_SIZE;
  return [u0, 1 - vBot, u1, 1 - vTop];
}

function canvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d')!;
  return [c, g];
}

const rgb = (r: number, g: number, b: number, a = 1): string => `rgba(${r | 0},${g | 0},${b | 0},${a})`;

/** Speckle noise over a rect. */
function speckle(g: CanvasRenderingContext2D, rng: Rng, x: number, y: number, w: number, h: number, n: number, light: string, dark: string, size = 1.5): void {
  for (let i = 0; i < n; i++) {
    g.fillStyle = rng.chance(0.5) ? light : dark;
    const s = rng.range(0.6, size);
    g.fillRect(x + rng.next() * w, y + rng.next() * h, s, s);
  }
}

// ------------------------------------------------------------------------------------- wall bases

function paintBrick(g: CanvasRenderingContext2D, rng: Rng, x: number, y: number, w: number, h: number, soot = 0): void {
  g.fillStyle = rgb(118, 104, 92);
  g.fillRect(x, y, w, h);
  const bw = 16;
  const bh = 6;
  for (let row = 0; row * bh < h; row++) {
    const off = row % 2 === 0 ? 0 : bw / 2;
    for (let col = -1; col * bw < w + bw; col++) {
      const bx = x + col * bw + off;
      const by = y + row * bh;
      const t = rng.next();
      const r = 128 + t * 34 + rng.range(-8, 8);
      const gg = 62 + t * 18 + rng.range(-5, 5);
      const b = 48 + t * 12 + rng.range(-5, 5);
      const dark = rng.chance(0.08) ? 0.7 : 1;
      g.fillStyle = rgb(r * dark, gg * dark, b * dark);
      g.fillRect(Math.max(x, bx + 1), by + 1, Math.min(bw - 1.5, x + w - bx - 1), bh - 1.4);
    }
  }
  // Colour variation: a few broad warm / cool patches (different firings, old repairs) and the odd
  // efflorescence bloom, so a wall is not one even tone from the high camera.
  for (let i = 0; i < 4; i++) {
    const warm = rng.chance(0.5);
    g.fillStyle = warm ? rgb(150, 70, 44, rng.range(0.08, 0.16)) : rgb(90, 70, 70, rng.range(0.08, 0.16));
    g.beginPath();
    g.ellipse(x + rng.next() * w, y + rng.next() * h, rng.range(30, 90), rng.range(16, 50), rng.range(0, 3), 0, Math.PI * 2);
    g.fill();
  }
  if (rng.chance(0.5)) {
    g.fillStyle = rgb(214, 206, 190, rng.range(0.1, 0.2));
    g.beginPath();
    g.ellipse(x + rng.next() * w, y + h * rng.range(0.5, 0.95), rng.range(16, 40), rng.range(8, 20), 0, 0, Math.PI * 2);
    g.fill();
  }
  // Grime: darker towards the top and streaks running down from sills.
  const grad = g.createLinearGradient(0, y, 0, y + h);
  grad.addColorStop(0, rgb(20, 16, 14, 0.25 + soot * 0.35));
  grad.addColorStop(0.5, rgb(20, 16, 14, 0.05 + soot * 0.15));
  grad.addColorStop(1, rgb(20, 16, 14, 0.18 + soot * 0.2));
  g.fillStyle = grad;
  g.fillRect(x, y, w, h);
  for (let i = 0; i < 6 + soot * 10; i++) {
    g.fillStyle = rgb(15, 12, 10, rng.range(0.05, 0.15));
    g.fillRect(x + rng.next() * w, y + rng.next() * h * 0.6, rng.range(2, 8), rng.range(20, 90));
  }
  // Mortar joints: a faint lighter line under each course so the bond reads in raking light.
  g.fillStyle = rgb(150, 140, 128, 0.14);
  for (let row = 0; row * bh < h; row++) g.fillRect(x, y + row * bh, w, 1);
}

/** London stock brick: yellow-grey clamp bricks, lime mortar, heavy soot towards the top. */
function paintStock(g: CanvasRenderingContext2D, rng: Rng, x: number, y: number, w: number, h: number, soot = 0.6): void {
  g.fillStyle = rgb(132, 124, 106);
  g.fillRect(x, y, w, h);
  const bw = 16;
  const bh = 6;
  for (let row = 0; row * bh < h; row++) {
    const off = row % 2 === 0 ? 0 : bw / 2;
    for (let col = -1; col * bw < w + bw; col++) {
      const bx = x + col * bw + off;
      const by = y + row * bh;
      const t = rng.next();
      // mostly buff/yellow, the odd purple-grey or burnt clinker
      const odd = rng.chance(0.1);
      const r = odd ? 104 + t * 20 : 150 + t * 28 + rng.range(-8, 8);
      const gg = odd ? 90 + t * 14 : 128 + t * 22 + rng.range(-6, 6);
      const b = odd ? 86 + t * 12 : 84 + t * 16 + rng.range(-6, 6);
      g.fillStyle = rgb(r, gg, b);
      g.fillRect(Math.max(x, bx + 1), by + 1, Math.min(bw - 1.5, x + w - bx - 1), bh - 1.4);
    }
  }
  // Patches of darker, sootier brick and the odd repointed (paler) area.
  for (let i = 0; i < 4; i++) {
    const dark = rng.chance(0.65);
    g.fillStyle = dark ? rgb(60, 52, 46, rng.range(0.1, 0.2)) : rgb(200, 190, 168, rng.range(0.08, 0.14));
    g.beginPath();
    g.ellipse(x + rng.next() * w, y + rng.next() * h, rng.range(30, 90), rng.range(16, 50), rng.range(0, 3), 0, Math.PI * 2);
    g.fill();
  }
  const grad = g.createLinearGradient(0, y, 0, y + h);
  grad.addColorStop(0, rgb(26, 22, 18, 0.32 + soot * 0.3));
  grad.addColorStop(0.55, rgb(26, 22, 18, 0.08 + soot * 0.12));
  grad.addColorStop(1, rgb(26, 22, 18, 0.2 + soot * 0.15));
  g.fillStyle = grad;
  g.fillRect(x, y, w, h);
  for (let i = 0; i < 8 + soot * 10; i++) {
    g.fillStyle = rgb(20, 18, 14, rng.range(0.05, 0.16));
    g.fillRect(x + rng.next() * w, y + rng.next() * h * 0.6, rng.range(2, 8), rng.range(20, 90));
  }
  g.fillStyle = rgb(170, 162, 146, 0.14);
  for (let row = 0; row * bh < h; row++) g.fillRect(x, y + row * bh, w, 1);
}

function paintStucco(g: CanvasRenderingContext2D, rng: Rng, x: number, y: number, w: number, h: number): void {
  g.fillStyle = rgb(196, 186, 162);
  g.fillRect(x, y, w, h);
  speckle(g, rng, x, y, w, h, 900, rgb(210, 200, 178, 0.5), rgb(150, 140, 120, 0.4));
  // rusticated lines
  g.strokeStyle = rgb(150, 140, 118, 0.35);
  g.lineWidth = 1;
  for (let yy = y + 20; yy < y + h; yy += 26) {
    g.beginPath();
    g.moveTo(x, yy);
    g.lineTo(x + w, yy);
    g.stroke();
  }
  const grad = g.createLinearGradient(0, y, 0, y + h);
  grad.addColorStop(0, rgb(40, 34, 30, 0.25));
  grad.addColorStop(1, rgb(40, 34, 30, 0.1));
  g.fillStyle = grad;
  g.fillRect(x, y, w, h);
}

function paintStone(g: CanvasRenderingContext2D, rng: Rng, x: number, y: number, w: number, h: number): void {
  g.fillStyle = rgb(150, 146, 136);
  g.fillRect(x, y, w, h);
  const bh = 22;
  for (let row = 0; row * bh < h; row++) {
    const off = row % 2 ? 30 : 0;
    for (let col = -1; col * 60 < w + 60; col++) {
      const t = rng.range(-12, 12);
      g.fillStyle = rgb(158 + t, 153 + t, 142 + t);
      g.fillRect(x + col * 60 + off + 1, y + row * bh + 1, 58, bh - 2);
    }
  }
  speckle(g, rng, x, y, w, h, 700, rgb(190, 186, 176, 0.4), rgb(90, 88, 80, 0.3));
  const grad = g.createLinearGradient(0, y, 0, y + h);
  grad.addColorStop(0, rgb(30, 30, 30, 0.35));
  grad.addColorStop(1, rgb(30, 30, 30, 0.12));
  g.fillStyle = grad;
  g.fillRect(x, y, w, h);
}

function paintConcrete(g: CanvasRenderingContext2D, rng: Rng, x: number, y: number, w: number, h: number): void {
  g.fillStyle = rgb(138, 136, 128);
  g.fillRect(x, y, w, h);
  speckle(g, rng, x, y, w, h, 1500, rgb(160, 158, 150, 0.5), rgb(100, 98, 92, 0.5));
  g.strokeStyle = rgb(80, 78, 72, 0.6);
  g.lineWidth = 2;
  g.strokeRect(x + 1, y + 1, w - 2, h - 2);
  for (let i = 0; i < 8; i++) {
    g.fillStyle = rgb(60, 58, 54, rng.range(0.06, 0.16));
    g.fillRect(x + rng.next() * w, y, rng.range(3, 10), h);
  }
}

function paintCorrugated(g: CanvasRenderingContext2D, rng: Rng, x: number, y: number, w: number, h: number, horizontal = false): void {
  const base = rng.range(0, 1);
  for (let i = 0; i < (horizontal ? h : w); i += 8) {
    const lum = 104 + Math.sin(i * 0.8) * 34;
    g.fillStyle = rgb(lum + base * 10, lum + 2, lum + 6);
    if (horizontal) g.fillRect(x, y + i, w, 8);
    else g.fillRect(x + i, y, 8, h);
  }
  // rust blooms
  for (let k = 0; k < 22; k++) {
    g.fillStyle = rgb(110, 58, 30, rng.range(0.08, 0.22));
    const rx = x + rng.next() * w;
    const ry = y + rng.next() * h;
    // rust runs in streaks along the corrugations
    if (horizontal) g.fillRect(rx, ry, rng.range(10, 60), rng.range(2, 6));
    else g.fillRect(rx, ry, rng.range(2, 6), rng.range(10, 60));
  }
}

// ------------------------------------------------------------------------------------- openings

type Glass = 'dark' | 'lit' | 'tv' | 'lit2' | 'shop' | 'stained' | 'cool' | 'dim' | 'fluoro';

function glassFill(g: CanvasRenderingContext2D, e: CanvasRenderingContext2D, rng: Rng, x: number, y: number, w: number, h: number, kind: Glass): void {
  if (kind === 'dark') {
    const grad = g.createLinearGradient(x, y, x + w, y + h);
    grad.addColorStop(0, rgb(46, 56, 70));
    grad.addColorStop(0.45, rgb(24, 28, 36));
    grad.addColorStop(0.55, rgb(58, 66, 80));
    grad.addColorStop(1, rgb(18, 20, 26));
    g.fillStyle = grad;
    g.fillRect(x, y, w, h);
    e.fillStyle = '#000';
    e.fillRect(x, y, w, h);
    return;
  }
  const palette: Record<Exclude<Glass, 'dark'>, [number, number, number]> = {
    lit: [255, 188, 104],
    lit2: [255, 160, 82],
    tv: [150, 190, 255],
    shop: [255, 226, 170],
    stained: [255, 140, 70],
    cool: [210, 225, 255],
    dim: [190, 110, 52],
    fluoro: [205, 240, 228],
  };
  const [r, gg, b] = palette[kind];
  g.fillStyle = rgb(r * 0.35, gg * 0.3, b * 0.25);
  g.fillRect(x, y, w, h);
  const grad = e.createLinearGradient(x, y, x, y + h);
  grad.addColorStop(0, rgb(r * 0.75, gg * 0.7, b * 0.65));
  grad.addColorStop(1, rgb(r, gg, b));
  e.fillStyle = grad;
  e.fillRect(x, y, w, h);
  if (kind === 'lit' || kind === 'lit2' || kind === 'tv' || kind === 'dim') {
    // Curtains: darker side panels, occasionally a silhouette.
    const cw = w * rng.range(0.15, 0.3);
    e.fillStyle = rgb(r * 0.45, gg * 0.32, b * 0.22);
    e.fillRect(x, y, cw, h);
    e.fillRect(x + w - cw, y, cw, h);
    g.fillStyle = rgb(r * 0.25, gg * 0.18, b * 0.12);
    g.fillRect(x, y, cw, h);
    g.fillRect(x + w - cw, y, cw, h);
    if (rng.chance(0.25)) {
      e.fillStyle = rgb(40, 24, 14);
      const sx = x + w * rng.range(0.35, 0.6);
      e.beginPath();
      e.ellipse(sx, y + h * 0.42, w * 0.08, h * 0.09, 0, 0, Math.PI * 2);
      e.fill();
      e.fillRect(sx - w * 0.12, y + h * 0.5, w * 0.24, h * 0.5);
    }
  }
  if (kind === 'stained') {
    const cols = [
      [255, 90, 60],
      [80, 140, 255],
      [255, 200, 80],
      [90, 220, 140],
    ];
    for (let yy = y; yy < y + h; yy += 10) {
      for (let xx = x; xx < x + w; xx += 9) {
        const c = cols[rng.int(0, 3)]!;
        e.fillStyle = rgb(c[0]! * 0.8, c[1]! * 0.8, c[2]! * 0.8);
        e.fillRect(xx + 1, yy + 1, 8, 9);
      }
    }
  }
}

/** Victorian sash window: stone lintel + sill, white frame, glazing bars. */
function sashWindow(g: CanvasRenderingContext2D, e: CanvasRenderingContext2D, rng: Rng, cx: number, top: number, w: number, h: number, kind: Glass, frame = rgb(214, 208, 192)): void {
  const x = cx - w / 2;
  // Reveal: the opening sits a brick back from the face. Lit from the upper left, so the left and top
  // reveals are in shadow and the right one catches a little light; the glass is set back again.
  g.fillStyle = rgb(20, 16, 14);
  g.fillRect(x - 7, top - 6, w + 12, h + 10);
  g.fillStyle = rgb(74, 62, 54);
  g.fillRect(x + w + 2, top - 2, 3, h + 4);
  g.fillStyle = rgb(8, 6, 6, 0.55);
  g.fillRect(x - 7, top - 6, 5, h + 10);
  g.fillRect(x - 7, top - 6, w + 12, 5);
  glassFill(g, e, rng, x, top, w, h, kind);
  // the top of the glass reflects the sky a touch more
  g.fillStyle = rgb(120, 140, 170, 0.12);
  g.fillRect(x, top, w, h * 0.25);
  g.fillStyle = frame;
  g.fillRect(x, top, w, 4);
  g.fillRect(x, top + h - 4, w, 4);
  g.fillRect(x, top, 4, h);
  g.fillRect(x + w - 4, top, 4, h);
  g.fillRect(x, top + h / 2 - 2, w, 4); // meeting rail
  g.fillRect(cx - 1.5, top, 3, h); // glazing bar
  // the upper sash stands proud of the lower: a shadow under the meeting rail
  g.fillStyle = rgb(0, 0, 0, 0.3);
  g.fillRect(x + 4, top + h / 2 + 2, w - 8, 2);
  // lintel: a stone slab with a lit top edge and a shadow line beneath
  g.fillStyle = rgb(176, 168, 150);
  g.fillRect(x - 8, top - 16, w + 16, 11);
  g.fillStyle = rgb(206, 198, 180);
  g.fillRect(x - 8, top - 16, w + 16, 2);
  g.fillStyle = rgb(30, 24, 20, 0.55);
  g.fillRect(x - 8, top - 6, w + 16, 2);
  // sill: projecting stone, bright top face, dark drip shadow running down the wall
  g.fillStyle = rgb(186, 178, 160);
  g.fillRect(x - 7, top + h + 1, w + 14, 7);
  g.fillStyle = rgb(222, 214, 196);
  g.fillRect(x - 7, top + h + 1, w + 14, 2);
  const drip = g.createLinearGradient(0, top + h + 8, 0, top + h + 22);
  drip.addColorStop(0, rgb(20, 16, 14, 0.6));
  drip.addColorStop(1, rgb(20, 16, 14, 0));
  g.fillStyle = drip;
  g.fillRect(x - 7, top + h + 8, w + 14, 14);
  // damp / dirt streaks under the sill ends
  g.fillStyle = rgb(30, 26, 22, 0.25);
  g.fillRect(x - 6, top + h + 8, 3, 26);
  g.fillRect(x + w + 3, top + h + 8, 3, 22);
}

/** Net curtains over the lower sash (and sometimes the upper): readable by day from the high camera. */
function nets(g: CanvasRenderingContext2D, rng: Rng, cx: number, top: number, w: number, h: number, full: boolean): void {
  const x = cx - w / 2 + 4;
  const y0 = full ? top + 4 : top + h / 2 + 2;
  const hh = full ? h - 8 : h / 2 - 6;
  g.fillStyle = rgb(214, 210, 198, 0.78);
  g.fillRect(x, y0, w - 8, hh);
  g.fillStyle = rgb(160, 156, 146, 0.5);
  for (let xx = x + 3; xx < x + w - 8; xx += 6) g.fillRect(xx, y0, 1.5, hh);
  // scalloped hem
  g.fillStyle = rgb(236, 232, 222, 0.85);
  for (let xx = x; xx < x + w - 10; xx += 10) {
    g.beginPath();
    g.arc(xx + 5, y0 + hh, 5, 0, Math.PI);
    g.fill();
  }
  if (rng.chance(0.5)) {
    // an ornament or plant on the sill
    g.fillStyle = rng.chance(0.5) ? rgb(60, 90, 50) : rgb(150, 60, 40);
    g.fillRect(cx - 7, top + h - 16, 14, 12);
  }
}

/** Gauged red-brick flat arch over a stock-brick opening (the classic London terrace detail). */
function gaugedArch(g: CanvasRenderingContext2D, cx: number, top: number, w: number): void {
  g.fillStyle = rgb(150, 66, 48);
  g.beginPath();
  g.moveTo(cx - w / 2 - 10, top - 2);
  g.lineTo(cx + w / 2 + 10, top - 2);
  g.lineTo(cx + w / 2 + 4, top - 18);
  g.lineTo(cx - w / 2 - 4, top - 18);
  g.closePath();
  g.fill();
  g.strokeStyle = rgb(110, 46, 34, 0.7);
  g.lineWidth = 1;
  for (let i = -5; i <= 5; i++) {
    g.beginPath();
    g.moveTo(cx + (i * w) / 11, top - 2);
    g.lineTo(cx + (i * (w + 8)) / 11, top - 18);
    g.stroke();
  }
}

function archWindow(g: CanvasRenderingContext2D, e: CanvasRenderingContext2D, rng: Rng, cx: number, top: number, w: number, h: number, kind: Glass): void {
  const x = cx - w / 2;
  g.save();
  e.save();
  const path = (c: CanvasRenderingContext2D): void => {
    c.beginPath();
    c.moveTo(x, top + h);
    c.lineTo(x, top + w / 2);
    c.arc(cx, top + w / 2, w / 2, Math.PI, 0);
    c.lineTo(x + w, top + h);
    c.closePath();
  };
  // brick arch voussoirs
  g.fillStyle = rgb(90, 50, 40);
  g.beginPath();
  g.arc(cx, top + w / 2, w / 2 + 7, Math.PI, 0);
  g.fill();
  path(g);
  g.clip();
  path(e);
  e.clip();
  glassFill(g, e, rng, x, top, w, h, kind);
  g.fillStyle = rgb(60, 62, 64);
  for (let yy = top + 14; yy < top + h; yy += 16) g.fillRect(x, yy, w, 2.5);
  for (let xx = x + w / 3; xx < x + w - 2; xx += w / 3) g.fillRect(xx - 1, top, 2.5, h);
  g.restore();
  e.restore();
  g.fillStyle = rgb(170, 162, 146);
  g.fillRect(x - 4, top + h, w + 8, 6);
}

function door(g: CanvasRenderingContext2D, e: CanvasRenderingContext2D, rng: Rng, cx: number, w: number, h: number, bottom: number, colour: string, fanlightLit = true): void {
  const x = cx - w / 2;
  const top = bottom - h;
  g.fillStyle = rgb(18, 14, 12);
  g.fillRect(x - 4, top - 22, w + 8, h + 22);
  // fanlight
  glassFill(g, e, rng, x, top - 18, w, 14, fanlightLit ? 'lit' : 'dark');
  g.fillStyle = colour;
  g.fillRect(x, top, w, h);
  g.strokeStyle = rgb(0, 0, 0, 0.45);
  g.lineWidth = 2;
  g.strokeRect(x + 6, top + 8, w / 2 - 9, h / 2 - 12);
  g.strokeRect(x + w / 2 + 3, top + 8, w / 2 - 9, h / 2 - 12);
  g.strokeRect(x + 6, top + h / 2 + 4, w / 2 - 9, h / 2 - 12);
  g.strokeRect(x + w / 2 + 3, top + h / 2 + 4, w / 2 - 9, h / 2 - 12);
  g.fillStyle = rgb(210, 180, 90);
  g.fillRect(x + w - 12, top + h * 0.55, 4, 4);
  // step
  g.fillStyle = rgb(170, 166, 156);
  g.fillRect(x - 8, bottom - 5, w + 16, 5);
}

function shopWindow(g: CanvasRenderingContext2D, e: CanvasRenderingContext2D, rng: Rng, x: number, y: number, w: number, h: number, variant: number): void {
  g.fillStyle = rgb(30, 26, 22);
  g.fillRect(x, y, w, h);
  // stall riser: glazed tiles (bottle green or oxblood) with a stone plinth
  g.fillStyle = variant === 1 ? rgb(70, 26, 22) : rgb(24, 52, 40);
  g.fillRect(x, y + h - 26, w, 26);
  g.fillStyle = rgb(255, 255, 255, 0.08);
  for (let xx = x; xx < x + w; xx += 16) g.fillRect(xx, y + h - 26, 1, 20);
  g.fillStyle = rgb(120, 114, 102);
  g.fillRect(x, y + h - 6, w, 6);
  // the reveal in shadow down the left and under the transom
  g.fillStyle = rgb(0, 0, 0, 0.4);
  g.fillRect(x, y, 6, h - 26);
  g.fillRect(x, y, w, 6);
  glassFill(g, e, rng, x + 6, y + 6, w - 12, h - 36, 'shop');
  // sunblind shadow on the top of the glass
  g.fillStyle = rgb(0, 0, 0, 0.25);
  g.fillRect(x + 6, y + 6, w - 12, 14);
  // goods silhouettes on shelves
  for (let shelf = 0; shelf < 3; shelf++) {
    const sy = y + 20 + shelf * ((h - 50) / 3);
    for (let xx = x + 10; xx < x + w - 14; xx += rng.range(8, 18)) {
      const ph = rng.range(6, 18);
      const c = variant === 1 ? [150, 70, 40] : [90 + rng.range(0, 80), 60 + rng.range(0, 60), 40 + rng.range(0, 40)];
      e.fillStyle = rgb(c[0]! * 0.7, c[1]! * 0.7, c[2]! * 0.7);
      e.fillRect(xx, sy + 20 - ph, rng.range(5, 10), ph);
      g.fillStyle = rgb(c[0]! * 0.4, c[1]! * 0.4, c[2]! * 0.4);
      g.fillRect(xx, sy + 20 - ph, 6, ph);
    }
  }
  g.fillStyle = rgb(170, 140, 70);
  g.fillRect(x, y, w, 4);
  g.fillRect(x + w / 2 - 2, y, 4, h - 26);
}

// ------------------------------------------------------------------------------------- the atlas

export interface FacadeAtlas {
  map: THREE.Texture;
  emissive: THREE.Texture;
  /** 8 × 8 texture of every tile's mean colour (the building detail pass scales its roof patterns to it). */
  means: THREE.Texture;
  /**
   * Baked atlas only: resolves with the loaded set once it should replace `map` / `emissive` / `means` (which
   * start as 4 × 4 placeholders so the material compiles with a map from the first frame), or with the
   * canvas-painted set when the file is missing or the GPU cannot take it.
   */
  ready?: Promise<{ map: THREE.Texture; emissive: THREE.Texture; means: THREE.Texture }>;
}

const LIN = Float32Array.from({ length: 256 }, (_, i) => {
  const v = i / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
});
const toSrgb = (v: number): number => Math.round(255 * (v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055));

/**
 * The tile-means texture from per-tile sRGB bytes in tile-id order: 8 × 8, nearest, rows flipped so that the
 * shader's `(tile + 0.5) / 8` lookup (v up, as the atlas uvs) lands on the right tile.
 */
export function meansTexture(rgb: ArrayLike<number>): THREE.DataTexture {
  const d = new Uint8Array(TILES_PER_ROW * TILES_PER_ROW * 4);
  for (let id = 0; id < TILES_PER_ROW * TILES_PER_ROW; id++) {
    const cx = id % TILES_PER_ROW;
    const row = Math.floor(id / TILES_PER_ROW);
    const o = ((TILES_PER_ROW - 1 - row) * TILES_PER_ROW + cx) * 4;
    d[o] = rgb[id * 3] ?? 128;
    d[o + 1] = rgb[id * 3 + 1] ?? 128;
    d[o + 2] = rgb[id * 3 + 2] ?? 128;
    d[o + 3] = 255;
  }
  const t = new THREE.DataTexture(d, TILES_PER_ROW, TILES_PER_ROW);
  t.colorSpace = THREE.SRGBColorSpace;
  t.magFilter = t.minFilter = THREE.NearestFilter;
  t.needsUpdate = true;
  return t;
}

/** Every tile's mean colour (linear average, like a GPU mip) of a painted atlas canvas. */
export function tileMeans(c: HTMLCanvasElement): THREE.DataTexture {
  const g = c.getContext('2d', { willReadFrequently: true })!;
  const out = new Uint8Array(TILES_PER_ROW * TILES_PER_ROW * 3);
  for (let id = 0; id < TILES_PER_ROW * TILES_PER_ROW; id++) {
    const px = g.getImageData((id % TILES_PER_ROW) * TILE_PX, Math.floor(id / TILES_PER_ROW) * TILE_PX, TILE_PX, TILE_PX).data;
    let r = 0;
    let gg = 0;
    let b = 0;
    for (let i = 0; i < px.length; i += 4) {
      r += LIN[px[i]!]!;
      gg += LIN[px[i + 1]!]!;
      b += LIN[px[i + 2]!]!;
    }
    const n = px.length / 4;
    out[id * 3] = toSrgb(r / n);
    out[id * 3 + 1] = toSrgb(gg / n);
    out[id * 3 + 2] = toSrgb(b / n);
  }
  return meansTexture(out);
}

/**
 * The facade atlas from public/textures/facade-atlas.ktx2 + facade-emissive.ktx2 (the default; `?canvasfacade`
 * paints the canvases at boot as before, for A/B)
 * (scripts/build-facade-atlas.mjs: the canvas painter's tiles with the Blender-baked terrace tiles dropped in,
 * docs/research/BLENDER-BUILDINGS.md P0 / P1) instead of painting the canvases at boot. Same 2048² grid of
 * 256 px tiles, so tileUV() and every builder are untouched; compressed in GPU memory (ASTC / ETC2 / BC via
 * render/ktx2.ts) where the canvases cost 21 MB each with mips.
 */
export const BAKED_FACADE = typeof location === 'undefined' || !new URLSearchParams(location.search).has('canvasfacade');

const BASE = import.meta.env?.BASE_URL ?? './';

function placeholder(r: number, g: number, b: number): THREE.DataTexture {
  const d = new Uint8Array(4 * 4 * 4);
  for (let i = 0; i < d.length; i += 4) d.set([r, g, b, 255], i);
  const t = new THREE.DataTexture(d, 4, 4);
  t.colorSpace = THREE.SRGBColorSpace;
  t.needsUpdate = true;
  return t;
}

/** The baked atlas (see BAKED_FACADE): placeholders now, the KTX2 pair (or the canvas fallback) through `ready`. */
export function createFacadeAtlasBaked(): FacadeAtlas {
  const map = placeholder(110, 90, 80);
  const emissive = placeholder(0, 0, 0);
  const means = meansTexture([]);
  const ready = (async (): Promise<{ map: THREE.Texture; emissive: THREE.Texture; means: THREE.Texture }> => {
    try {
      const loader = ktx2Loader();
      const [m, e, mean] = await Promise.all([
        loader.loadAsync(`${BASE}textures/facade-atlas.ktx2`),
        loader.loadAsync(`${BASE}textures/facade-emissive.ktx2`),
        fetch(`${BASE}textures/facade-means.json`).then((r) => (r.ok ? (r.json() as Promise<number[]>) : Promise.reject(new Error(r.statusText)))),
      ]);
      for (const t of [m, e]) {
        t.colorSpace = THREE.SRGBColorSpace;
        t.anisotropy = 8;
        t.needsUpdate = true;
      }
      return { map: m, emissive: e, means: meansTexture(mean) };
    } catch {
      // no file (or no KTX2 support): the painter as before
      const painted = createFacadeAtlas();
      return { map: painted.map, emissive: painted.emissive, means: painted.means };
    }
  })();
  return { map, emissive, means, ready };
}

/**
 * The facade relief atlas (docs/research/BLENDER-BUILDINGS.md P4): public/textures/facade-relief.ktx2, the tile grid
 * at half density with R, G = the tangent-space normal's x, y (Blender-baked tiles: the shaders' bump; canvas tiles: the
 * slope of the luminance) and B = ambient occlusion. HIGH only: materials.ts binds it as a packed normal map and reads
 * the occlusion from B. Null when the file is missing or KTX2 is not supported.
 */
export async function loadFacadeRelief(): Promise<THREE.Texture | null> {
  try {
    const t = await ktx2Loader().loadAsync(`${BASE}textures/facade-relief.ktx2`);
    t.colorSpace = THREE.NoColorSpace;
    t.anisotropy = 4;
    t.needsUpdate = true;
    return t;
  } catch {
    return null;
  }
}

export function createFacadeAtlas(): FacadeAtlas {
  const [mc, g] = canvas(ATLAS_SIZE, ATLAS_SIZE);
  const [ec, e] = canvas(ATLAS_SIZE, ATLAS_SIZE);
  e.fillStyle = '#000';
  e.fillRect(0, 0, ATLAS_SIZE, ATLAS_SIZE);
  const rng = new Rng(4242);
  const S = TILE_PX;
  const tile = (id: number, paint: (x: number, y: number) => void): void => {
    const x = (id % TILES_PER_ROW) * S;
    const y = Math.floor(id / TILES_PER_ROW) * S;
    g.save();
    e.save();
    g.beginPath();
    g.rect(x, y, S, S);
    g.clip();
    e.beginPath();
    e.rect(x, y, S, S);
    e.clip();
    paint(x, y);
    g.restore();
    e.restore();
  };
  const brickWin = (x: number, y: number, kind: Glass, soot = 0): void => {
    paintBrick(g, rng, x, y, S, S, soot);
    sashWindow(g, e, rng, x + S / 2, y + 52, 92, 150, kind);
  };
  tile(T.brickPlain, (x, y) => paintBrick(g, rng, x, y, S, S));
  tile(T.brickSoot, (x, y) => paintBrick(g, rng, x, y, S, S, 1));
  tile(T.brickGable, (x, y) => {
    paintBrick(g, rng, x, y, S, S, 0.6);
    g.fillStyle = rgb(30, 24, 20);
    g.fillRect(x + S / 2 - 10, y + 60, 20, 26);
  });
  tile(T.brickWinDark, (x, y) => brickWin(x, y, 'dark'));
  tile(T.brickWinDark2, (x, y) => {
    brickWin(x, y, 'dark', 0.4);
    g.fillStyle = rgb(190, 180, 150);
    g.fillRect(x + S / 2 - 42, y + 56, 84, 40);
  });
  tile(T.brickWinLit, (x, y) => brickWin(x, y, 'lit'));
  tile(T.brickWinLit2, (x, y) => brickWin(x, y, 'lit2'));
  tile(T.brickWinTV, (x, y) => brickWin(x, y, 'tv'));
  tile(T.brickDoor, (x, y) => {
    paintBrick(g, rng, x, y, S, S);
    door(g, e, rng, x + S / 2, 78, 168, y + S, rgb(40, 40, 40));
  });
  tile(T.brickArchWinDark, (x, y) => {
    paintBrick(g, rng, x, y, S, S, 0.7);
    archWindow(g, e, rng, x + S / 2, y + 60, 96, 140, 'dark');
  });
  tile(T.brickArchWinLit, (x, y) => {
    paintBrick(g, rng, x, y, S, S, 0.7);
    archWindow(g, e, rng, x + S / 2, y + 60, 96, 140, 'lit2');
  });
  tile(T.loadingDoor, (x, y) => {
    paintBrick(g, rng, x, y, S, S, 0.8);
    g.fillStyle = rgb(20, 16, 14);
    g.fillRect(x + 54, y + 30, 148, S - 30);
    g.fillStyle = rgb(70, 52, 38);
    g.fillRect(x + 60, y + 36, 66, S - 40);
    g.fillRect(x + 130, y + 36, 66, S - 40);
    g.strokeStyle = rgb(40, 30, 22);
    g.lineWidth = 3;
    for (let yy = y + 50; yy < y + S; yy += 18) {
      g.beginPath();
      g.moveTo(x + 60, yy);
      g.lineTo(x + 196, yy);
      g.stroke();
    }
    g.fillStyle = rgb(170, 160, 140);
    g.fillRect(x + 48, y + 22, 160, 9);
  });
  tile(T.stuccoPlain, (x, y) => paintStucco(g, rng, x, y, S, S));
  tile(T.stuccoWinDark, (x, y) => {
    paintStucco(g, rng, x, y, S, S);
    sashWindow(g, e, rng, x + S / 2, y + 50, 90, 154, 'dark', rgb(236, 232, 220));
  });
  tile(T.stuccoWinLit, (x, y) => {
    paintStucco(g, rng, x, y, S, S);
    sashWindow(g, e, rng, x + S / 2, y + 50, 90, 154, 'lit', rgb(236, 232, 220));
  });
  tile(T.stuccoDoor, (x, y) => {
    paintStucco(g, rng, x, y, S, S);
    door(g, e, rng, x + S / 2, 80, 168, y + S, rgb(30, 50, 40));
  });
  tile(T.shopWinLit, (x, y) => shopWindow(g, e, rng, x, y + 20, S, S - 20, 0));
  tile(T.shopWinLit2, (x, y) => shopWindow(g, e, rng, x, y + 20, S, S - 20, 1));
  tile(T.shopWinDark, (x, y) => {
    g.fillStyle = rgb(30, 26, 22);
    g.fillRect(x, y, S, S);
    glassFill(g, e, rng, x + 6, y + 26, S - 12, S - 56, 'dark');
    g.fillStyle = rgb(60, 40, 30);
    g.fillRect(x, y + S - 26, S, 26);
  });
  tile(T.shopShutter, (x, y) => {
    for (let yy = y; yy < y + S; yy += 7) {
      g.fillStyle = rgb(120 + Math.sin(yy) * 8, 122, 124);
      g.fillRect(x, yy, S, 6);
      g.fillStyle = rgb(70, 70, 72);
      g.fillRect(x, yy + 6, S, 1);
    }
    g.fillStyle = rgb(40, 40, 40, 0.5);
    g.fillRect(x, y, S, 18);
  });
  tile(T.shopDoorLit, (x, y) => {
    g.fillStyle = rgb(30, 26, 22);
    g.fillRect(x, y, S, S);
    glassFill(g, e, rng, x + 70, y + 30, S - 140, S - 40, 'shop');
    g.fillStyle = rgb(90, 60, 40);
    g.fillRect(x + 64, y + 24, S - 128, 8);
    g.fillRect(x + 64, y + S * 0.55, S - 128, 10);
  });
  tile(T.pubWinLit, (x, y) => {
    g.fillStyle = rgb(24, 46, 34);
    g.fillRect(x, y, S, S);
    glassFill(g, e, rng, x + 18, y + 40, S - 36, S - 80, 'lit2');
    // etched lower half
    e.fillStyle = rgb(150, 90, 40);
    e.fillRect(x + 18, y + S * 0.55, S - 36, S * 0.45 - 40);
    // By day (the glow is night-only, see materials.ts) the etched half reads as frosted glass with a
    // scrolled border, not as an orange lamp.
    const ey = y + S * 0.55;
    const eh = S * 0.45 - 40;
    g.fillStyle = rgb(92, 86, 70);
    g.fillRect(x + 18, ey, S - 36, eh);
    g.strokeStyle = rgb(60, 56, 46);
    g.lineWidth = 3;
    g.strokeRect(x + 26, ey + 8, S - 52, eh - 16);
    g.lineWidth = 2;
    for (let k = 0; k < 2; k++) {
      const cx = x + S / 4 + (k * S) / 2;
      g.beginPath();
      g.ellipse(cx, ey + eh / 2, S / 8, eh / 4, 0, 0, Math.PI * 2);
      g.stroke();
    }
    g.fillStyle = rgb(190, 160, 90);
    g.fillRect(x + 18, y + 40, S - 36, 3);
    g.fillRect(x + S / 2 - 2, y + 40, 4, S - 80);
    g.fillStyle = rgb(18, 34, 26);
    g.fillRect(x, y + S - 40, S, 40);
  });
  tile(T.pubDoor, (x, y) => {
    g.fillStyle = rgb(24, 46, 34);
    g.fillRect(x, y, S, S);
    door(g, e, rng, x + S / 2, 92, 176, y + S, rgb(60, 24, 20));
  });
  // striped canvas awnings, 0.22 m stripes over a 5 m run: the colour and cream (the quad is tinted near-white)
  const awningTile = (x: number, y: number, stripe: string): void => {
    for (let xx = 0; xx < S; xx += 11.3) {
      g.fillStyle = Math.round(xx / 11.3) % 2 === 0 ? stripe : rgb(232, 226, 210);
      g.fillRect(x + xx, y, 12, S);
    }
    g.fillStyle = rgb(0, 0, 0, 0.2);
    g.fillRect(x, y + S - 30, S, 30);
  };
  tile(T.awningBlue, (x, y) => awningTile(x, y, rgb(40, 66, 128)));
  tile(T.clubFront, (x, y) => {
    g.fillStyle = rgb(26, 18, 26);
    g.fillRect(x, y, S, S);
    for (let i = 0; i < 3; i++) {
      const cx = x + 50 + i * 78;
      g.fillStyle = rgb(10, 6, 10);
      g.beginPath();
      g.arc(cx, y + 110, 22, 0, Math.PI * 2);
      g.fill();
      e.fillStyle = rgb(230, 120, 160);
      e.beginPath();
      e.arc(cx, y + 110, 17, 0, Math.PI * 2);
      e.fill();
    }
    g.fillStyle = rgb(120, 30, 50);
    g.fillRect(x, y + S - 50, S, 6);
  });
  tile(T.corrugated, (x, y) => paintCorrugated(g, rng, x, y, S, S));
  tile(T.corrugatedRoof, (x, y) => paintCorrugated(g, rng, x, y, S, S, true));
  tile(T.garageDoor, (x, y) => {
    paintBrick(g, rng, x, y, S, S, 0.5);
    g.fillStyle = rgb(16, 14, 12);
    g.fillRect(x + 20, y + 40, S - 40, S - 40);
    for (let xx = x + 24; xx < x + S - 24; xx += 10) {
      g.fillStyle = rgb(90 + (xx % 20 === 4 ? 14 : 0), 96, 88);
      g.fillRect(xx, y + 44, 9, S - 44);
    }
    g.fillStyle = rgb(110, 60, 30, 0.35);
    g.fillRect(x + 24, y + S - 60, S - 48, 60);
    g.fillStyle = rgb(200, 200, 190);
    g.fillRect(x + S / 2 - 4, y + S - 70, 8, 4);
  });
  tile(T.rollerDoor, (x, y) => {
    paintBrick(g, rng, x, y, S, S, 0.8);
    g.fillStyle = rgb(20, 18, 16);
    g.fillRect(x + 16, y + 20, S - 32, S - 20);
    for (let yy = y + 24; yy < y + S; yy += 8) {
      g.fillStyle = rgb(104, 106, 100);
      g.fillRect(x + 20, yy, S - 40, 7);
    }
  });
  tile(T.concretePlain, (x, y) => paintConcrete(g, rng, x, y, S, S));
  tile(T.flatsWinDark, (x, y) => {
    paintConcrete(g, rng, x, y, S, S);
    g.fillStyle = rgb(20, 20, 22);
    g.fillRect(x + 20, y + 60, S - 40, 120);
    glassFill(g, e, rng, x + 24, y + 64, S - 48, 112, 'dark');
    g.fillStyle = rgb(170, 170, 160);
    for (let xx = x + 24; xx < x + S - 24; xx += (S - 48) / 3) g.fillRect(xx, y + 64, 3, 112);
  });
  tile(T.flatsWinLit, (x, y) => {
    paintConcrete(g, rng, x, y, S, S);
    g.fillStyle = rgb(20, 20, 22);
    g.fillRect(x + 20, y + 60, S - 40, 120);
    glassFill(g, e, rng, x + 24, y + 64, S - 48, 112, rng.chance(0.5) ? 'lit' : 'tv');
    g.fillStyle = rgb(170, 170, 160);
    for (let xx = x + 24; xx < x + S - 24; xx += (S - 48) / 3) g.fillRect(xx, y + 64, 3, 112);
  });
  tile(T.deckFront, (x, y) => {
    paintConcrete(g, rng, x, y, S, S);
    g.fillStyle = rgb(60, 70, 80);
    for (let xx = x; xx < x + S; xx += 12) g.fillRect(xx, y + 40, 3, S - 40);
    g.fillStyle = rgb(180, 178, 170);
    g.fillRect(x, y + 36, S, 8);
  });
  tile(T.stonePlain, (x, y) => paintStone(g, rng, x, y, S, S));
  tile(T.stoneWinDark, (x, y) => {
    paintStone(g, rng, x, y, S, S);
    g.fillStyle = rgb(120, 116, 106);
    g.beginPath();
    g.moveTo(x + 70, y + 46);
    g.lineTo(x + S / 2, y + 22);
    g.lineTo(x + S - 70, y + 46);
    g.fill();
    sashWindow(g, e, rng, x + S / 2, y + 54, 96, 158, 'dark', rgb(230, 226, 214));
  });
  tile(T.stoneWinLit, (x, y) => {
    paintStone(g, rng, x, y, S, S);
    g.fillStyle = rgb(120, 116, 106);
    g.beginPath();
    g.moveTo(x + 70, y + 46);
    g.lineTo(x + S / 2, y + 22);
    g.lineTo(x + S - 70, y + 46);
    g.fill();
    sashWindow(g, e, rng, x + S / 2, y + 54, 96, 158, 'cool', rgb(230, 226, 214));
  });
  tile(T.stoneDoor, (x, y) => {
    paintStone(g, rng, x, y, S, S);
    g.fillStyle = rgb(20, 16, 14);
    g.beginPath();
    g.moveTo(x + 68, y + S);
    g.lineTo(x + 68, y + 90);
    g.arc(x + S / 2, y + 90, 60, Math.PI, 0);
    g.lineTo(x + S - 68, y + S);
    g.fill();
    g.fillStyle = rgb(70, 40, 26);
    g.fillRect(x + 76, y + 96, S - 152, S - 96);
    g.strokeStyle = rgb(30, 20, 14);
    g.lineWidth = 3;
    g.beginPath();
    g.moveTo(x + S / 2, y + 96);
    g.lineTo(x + S / 2, y + S);
    g.stroke();
  });
  tile(T.lancetLit, (x, y) => {
    paintStone(g, rng, x, y, S, S);
    g.save();
    e.save();
    const lancet = (c: CanvasRenderingContext2D): void => {
      c.beginPath();
      c.moveTo(x + 88, y + S - 20);
      c.lineTo(x + 88, y + 90);
      c.quadraticCurveTo(x + 88, y + 30, x + S / 2, y + 18);
      c.quadraticCurveTo(x + S - 88, y + 30, x + S - 88, y + 90);
      c.lineTo(x + S - 88, y + S - 20);
      c.closePath();
    };
    lancet(g);
    g.clip();
    lancet(e);
    e.clip();
    glassFill(g, e, rng, x + 80, y + 10, S - 160, S - 20, 'stained');
    g.restore();
    e.restore();
  });
  tile(T.factoryWinDark, (x, y) => {
    paintBrick(g, rng, x, y, S, S, 0.9);
    g.fillStyle = rgb(30, 30, 30);
    g.fillRect(x + 24, y + 40, S - 48, 160);
    glassFill(g, e, rng, x + 28, y + 44, S - 56, 152, 'dark');
    g.fillStyle = rgb(50, 54, 56);
    for (let xx = x + 28; xx < x + S - 28; xx += 20) g.fillRect(xx, y + 44, 3, 152);
    for (let yy = y + 44; yy < y + 196; yy += 19) g.fillRect(x + 28, yy, S - 56, 3);
  });
  tile(T.factoryWinLit, (x, y) => {
    paintBrick(g, rng, x, y, S, S, 0.9);
    g.fillStyle = rgb(30, 30, 30);
    g.fillRect(x + 24, y + 40, S - 48, 160);
    glassFill(g, e, rng, x + 28, y + 44, S - 56, 152, 'cool');
    g.fillStyle = rgb(50, 54, 56);
    e.fillStyle = rgb(20, 20, 20);
    for (let xx = x + 28; xx < x + S - 28; xx += 20) {
      g.fillRect(xx, y + 44, 3, 152);
      e.fillRect(xx, y + 44, 3, 152);
    }
    for (let yy = y + 44; yy < y + 196; yy += 19) {
      g.fillRect(x + 28, yy, S - 56, 3);
      e.fillRect(x + 28, yy, S - 56, 3);
    }
  });
  tile(T.slate, (x, y) => {
    // Welsh slate in courses: each slate a slightly different blue / purple grey, the tail of every
    // course throwing a shadow on the one below, the odd slipped or cracked slate, lichen and moss.
    g.fillStyle = rgb(34, 36, 42);
    g.fillRect(x, y, S, S);
    for (let row = 0; row * 12 < S; row++) {
      const off = row % 2 ? 10 : 0;
      for (let col = -1; col * 20 < S + 20; col++) {
        // a few tones apart, no more: at the gameplay distance a wider spread reads as a chequerboard
        const t = rng.range(-3, 3);
        const purple = rng.chance(0.14);
        const pale = rng.chance(0.02);
        g.fillStyle = pale ? rgb(78 + t, 82 + t, 90 + t) : purple ? rgb(72 + t, 70 + t, 82 + t) : rgb(68 + t, 73 + t, 83 + t);
        const sx = x + col * 20 + off + 1;
        const sy = y + row * 12;
        g.fillRect(sx, sy, 18, 11);
        // weathered top edge, darker lower edge (relief)
        g.fillStyle = rgb(255, 255, 255, 0.04);
        g.fillRect(sx, sy, 18, 2);
        g.fillStyle = rgb(0, 0, 0, 0.22);
        g.fillRect(sx, sy + 9, 18, 2);
        if (rng.chance(0.02)) {
          // slipped slate: the dark felt shows above it. Half-toned: the tile repeats across every
          // roof, and solid black notches lined up into a lattice of dashes from above.
          g.fillStyle = rgb(28, 26, 26, 0.42);
          g.fillRect(sx, sy, 18, 3);
        }
      }
    }
    // lichen (pale yellow-grey) and moss (green) spots
    for (let i = 0; i < 14; i++) {
      g.fillStyle = rgb(150, 150, 110, rng.range(0.06, 0.14));
      g.beginPath();
      g.ellipse(x + rng.next() * S, y + rng.next() * S, rng.range(4, 12), rng.range(3, 8), 0, 0, Math.PI * 2);
      g.fill();
    }
    for (let i = 0; i < 8; i++) {
      g.fillStyle = rgb(40, 70, 40, rng.range(0.06, 0.16));
      g.fillRect(x + rng.next() * S, y + rng.next() * S, rng.range(10, 40), rng.range(4, 12));
    }
  });
  tile(T.felt, (x, y) => {
    g.fillStyle = rgb(70, 68, 66);
    g.fillRect(x, y, S, S);
    speckle(g, rng, x, y, S, S, 2500, rgb(98, 96, 92, 0.6), rgb(48, 46, 44, 0.6), 2);
    // Broad, low-contrast wear. Small, dark discs repeated in every sheet read as polka dots from above.
    for (let i = 0; i < 4; i++) {
      g.fillStyle = rgb(44, 42, 42, rng.range(0.12, 0.24));
      g.beginPath();
      g.ellipse(x + rng.next() * S, y + rng.next() * S, rng.range(18, 50), rng.range(10, 30), rng.range(0, 3), 0, Math.PI * 2);
      g.fill();
    }
    g.strokeStyle = rgb(50, 48, 46, 0.45);
    g.lineWidth = 2;
    for (let yy = y + 40; yy < y + S; yy += 64) {
      g.beginPath();
      g.moveTo(x, yy);
      g.lineTo(x + S, yy + rng.range(-4, 4));
      g.stroke();
    }
    for (let i = 0; i < 5; i++) {
      g.fillStyle = rgb(80, 82, 86, rng.range(0.035, 0.09));
      g.beginPath();
      g.ellipse(x + rng.next() * S, y + rng.next() * S, rng.range(10, 40), rng.range(10, 30), 0, 0, Math.PI * 2);
      g.fill();
    }
  });
  tile(T.lead, (x, y) => {
    g.fillStyle = rgb(110, 116, 120);
    g.fillRect(x, y, S, S);
    for (let xx = x; xx < x + S; xx += 32) {
      g.fillStyle = rgb(80, 86, 90);
      g.fillRect(xx, y, 3, S);
    }
  });
  tile(T.white, (x, y) => {
    g.fillStyle = '#fff';
    g.fillRect(x, y, S, S);
  });
  tile(T.awning, (x, y) => awningTile(x, y, rgb(190, 46, 36)));
  tile(T.glazingLit, (x, y) => {
    g.fillStyle = rgb(40, 44, 48);
    g.fillRect(x, y, S, S);
    for (let yy = y; yy < y + S; yy += 32) {
      for (let xx = x; xx < x + S; xx += 32) {
        glassFill(g, e, rng, xx + 3, yy + 3, 26, 26, rng.chance(0.7) ? 'cool' : 'dark');
      }
    }
  });
  tile(T.glazingDark, (x, y) => {
    g.fillStyle = rgb(40, 44, 48);
    g.fillRect(x, y, S, S);
    for (let yy = y; yy < y + S; yy += 32) {
      for (let xx = x; xx < x + S; xx += 32) glassFill(g, e, rng, xx + 3, yy + 3, 26, 26, 'dark');
    }
  });
  tile(T.timber, (x, y) => {
    for (let xx = 0; xx < S; xx += 22) {
      const t = rng.range(-12, 12);
      g.fillStyle = rgb(92 + t, 70 + t, 50 + t);
      g.fillRect(x + xx, y, 21, S);
      g.fillStyle = rgb(40, 30, 22, 0.6);
      g.fillRect(x + xx + 21, y, 1, S);
    }
    speckle(g, rng, x, y, S, S, 400, rgb(120, 100, 80, 0.4), rgb(40, 30, 20, 0.4), 2);
  });
  const archBay = (x: number, y: number, inner: (ax: number, ay: number, aw: number, ah: number) => void): void => {
    paintBrick(g, rng, x, y, S, S, 1);
    const aw = 200;
    const ax = x + (S - aw) / 2;
    const ay = y + 40;
    g.fillStyle = rgb(70, 40, 32);
    g.beginPath();
    g.moveTo(ax - 10, y + S);
    g.lineTo(ax - 10, ay + aw / 2);
    g.arc(x + S / 2, ay + aw / 2, aw / 2 + 10, Math.PI, 0);
    g.lineTo(ax + aw + 10, y + S);
    g.fill();
    g.save();
    e.save();
    const p = (c: CanvasRenderingContext2D): void => {
      c.beginPath();
      c.moveTo(ax, y + S);
      c.lineTo(ax, ay + aw / 2);
      c.arc(x + S / 2, ay + aw / 2, aw / 2, Math.PI, 0);
      c.lineTo(ax + aw, y + S);
      c.closePath();
    };
    p(g);
    g.clip();
    p(e);
    e.clip();
    inner(ax, ay, aw, y + S - ay);
    g.restore();
    e.restore();
  };
  tile(T.archLockup, (x, y) =>
    archBay(x, y, (ax, ay, aw, ah) => {
      g.fillStyle = rgb(40, 34, 30);
      g.fillRect(ax, ay, aw, ah);
      for (let xx = ax; xx < ax + aw; xx += 11) {
        g.fillStyle = rgb(70 + rng.range(0, 14), 80, 70);
        g.fillRect(xx, ay + aw / 2 - 10, 10, ah);
      }
      g.fillStyle = rgb(30, 24, 20);
      g.fillRect(ax, ay, aw, aw / 2 - 10);
    }),
  );
  tile(T.archWorkshopLit, (x, y) =>
    archBay(x, y, (ax, ay, aw, ah) => {
      g.fillStyle = rgb(60, 50, 40);
      g.fillRect(ax, ay, aw, ah);
      glassFill(g, e, rng, ax, ay, aw, aw / 2 + 10, 'lit2');
      g.fillStyle = rgb(30, 26, 22);
      for (let xx = ax; xx < ax + aw; xx += 25) g.fillRect(xx, ay, 3, aw / 2 + 10);
      g.fillStyle = rgb(90, 40, 30);
      g.fillRect(ax + 20, ay + aw / 2 + 14, aw - 40, ah - aw / 2 - 14);
    }),
  );
  tile(T.archBlank, (x, y) =>
    archBay(x, y, (ax, ay, aw, ah) => {
      paintBrick(g, rng, ax, ay, aw, ah, 1);
      g.fillStyle = rgb(20, 14, 12, 0.35);
      g.fillRect(ax, ay, aw, ah);
    }),
  );
  tile(T.girder, (x, y) => {
    g.fillStyle = rgb(46, 58, 54);
    g.fillRect(x, y, S, S);
    g.fillStyle = rgb(36, 46, 42);
    g.fillRect(x, y, S, 24);
    g.fillRect(x, y + S - 24, S, 24);
    for (let xx = x + 8; xx < x + S; xx += 16) {
      g.fillStyle = rgb(70, 84, 78);
      g.beginPath();
      g.arc(xx, y + 12, 3, 0, Math.PI * 2);
      g.arc(xx, y + S - 12, 3, 0, Math.PI * 2);
      g.fill();
    }
    for (let xx = x; xx < x + S; xx += 64) {
      g.fillStyle = rgb(30, 38, 34);
      g.fillRect(xx, y + 24, 6, S - 48);
    }
    for (let i = 0; i < 12; i++) {
      g.fillStyle = rgb(110, 60, 30, rng.range(0.1, 0.3));
      g.fillRect(x + rng.next() * S, y + rng.next() * S, rng.range(4, 20), rng.range(10, 50));
    }
  });

  // ---- city pass 2 tiles (painted last so the earlier tiles keep their random sequence)
  const stockWin = (x: number, y: number, kind: Glass, withNets = false): void => {
    paintStock(g, rng, x, y, S, S);
    sashWindow(g, e, rng, x + S / 2, y + 52, 92, 150, kind, rgb(226, 222, 208));
    gaugedArch(g, x + S / 2, y + 50, 92);
    if (withNets) nets(g, rng, x + S / 2, y + 52, 92, 150, rng.chance(0.4));
  };
  tile(T.stockPlain, (x, y) => paintStock(g, rng, x, y, S, S));
  tile(T.stockWinDark, (x, y) => stockWin(x, y, 'dark'));
  tile(T.stockWinLit, (x, y) => stockWin(x, y, 'lit'));
  tile(T.stockWinLit2, (x, y) => stockWin(x, y, rng.chance(0.5) ? 'tv' : 'lit2'));
  tile(T.stockWinNets, (x, y) => stockWin(x, y, 'dark', true));
  tile(T.stockDoor, (x, y) => {
    paintStock(g, rng, x, y, S, S);
    door(g, e, rng, x + S / 2, 78, 168, y + S, rgb(40, 40, 40));
    gaugedArch(g, x + S / 2, y + S - 168 - 22, 86);
  });
  tile(T.brickWinNets, (x, y) => {
    paintBrick(g, rng, x, y, S, S, 0.2);
    sashWindow(g, e, rng, x + S / 2, y + 52, 92, 150, 'dark');
    nets(g, rng, x + S / 2, y + 52, 92, 150, rng.chance(0.4));
  });
  tile(T.brickWinDim, (x, y) => brickWin(x, y, 'dim', 0.3));
  tile(T.brickWinBoarded, (x, y) => {
    paintBrick(g, rng, x, y, S, S, 0.9);
    const bx = x + S / 2 - 46;
    g.fillStyle = rgb(20, 16, 14);
    g.fillRect(bx - 3, y + 50, 98, 154);
    // weathered corrugated / ply sheet nailed over the opening, with a fly-poster scrap
    for (let i = 0; i < 92; i += 8) {
      g.fillStyle = rgb(96 + Math.sin(i) * 14, 88 + Math.sin(i) * 10, 70);
      g.fillRect(bx + i, y + 52, 8, 150);
    }
    g.fillStyle = rgb(110, 58, 30, 0.35);
    g.fillRect(bx, y + 150, 92, 52);
    g.fillStyle = rgb(210, 200, 170, 0.8);
    g.fillRect(bx + 18, y + 80, 44, 56);
    g.fillStyle = rgb(160, 40, 30, 0.85);
    g.fillRect(bx + 22, y + 86, 36, 10);
    g.fillStyle = rgb(176, 168, 150);
    g.fillRect(bx - 6, y + 41, 104, 8);
  });
  tile(T.launderette, (x, y) => {
    // fluorescent-lit launderette: rows of front-loader portholes behind plate glass
    g.fillStyle = rgb(30, 30, 32);
    g.fillRect(x, y, S, S);
    glassFill(g, e, rng, x + 8, y + 40, S - 16, S - 80, 'fluoro');
    for (let i = 0; i < 4; i++) {
      const cx = x + 38 + i * 60;
      g.fillStyle = rgb(200, 200, 196);
      g.fillRect(cx - 24, y + 120, 48, 76);
      e.fillStyle = rgb(120, 130, 128);
      e.fillRect(cx - 24, y + 120, 48, 76);
      g.fillStyle = rgb(40, 50, 60);
      e.fillStyle = rgb(30, 60, 90);
      for (const c of [g, e]) {
        c.beginPath();
        c.arc(cx, y + 160, 16, 0, Math.PI * 2);
        c.fill();
      }
    }
    g.fillStyle = rgb(180, 180, 176);
    g.fillRect(x, y + 36, S, 5);
    g.fillStyle = rgb(70, 74, 80);
    g.fillRect(x, y + S - 40, S, 40);
  });
  tile(T.bookieWin, (x, y) => {
    // turf accountant: painted-out glass (by law you couldn't see in), gilt lettering, a dim glow above
    g.fillStyle = rgb(26, 30, 28);
    g.fillRect(x, y, S, S);
    g.fillStyle = rgb(36, 62, 48);
    g.fillRect(x + 12, y + 40, S - 24, S - 80);
    glassFill(g, e, rng, x + 12, y + 40, S - 24, 26, 'lit2');
    g.fillStyle = rgb(36, 62, 48);
    g.fillRect(x + 12, y + 66, S - 24, S - 106);
    g.fillStyle = rgb(206, 170, 90);
    g.font = `bold 22px Georgia, serif`;
    g.textAlign = 'center';
    g.fillText('ODDS', x + S / 2, y + 112);
    g.font = `bold 15px Georgia, serif`;
    g.fillText('ON ALL', x + S / 2, y + 140);
    g.fillText('MEETINGS', x + S / 2, y + 160);
    g.fillStyle = rgb(206, 170, 90);
    g.fillRect(x + 12, y + 40, S - 24, 3);
    g.fillStyle = rgb(20, 24, 22);
    g.fillRect(x, y + S - 40, S, 40);
  });
  tile(T.cafeWin, (x, y) => {
    // steamed-up café window, gingham half-curtains, a tea urn glinting
    g.fillStyle = rgb(30, 26, 22);
    g.fillRect(x, y, S, S);
    glassFill(g, e, rng, x + 10, y + 34, S - 20, S - 74, 'shop');
    e.fillStyle = rgb(255, 236, 200, 0.35);
    e.fillRect(x + 10, y + 34, S - 20, 60);
    const cy = y + 34 + (S - 74) * 0.55;
    for (let yy = cy; yy < y + S - 40; yy += 10) {
      for (let xx = x + 10; xx < x + S - 10; xx += 10) {
        const on = ((xx - x) / 10 + (yy - cy) / 10) % 2 < 1;
        g.fillStyle = on ? rgb(170, 40, 34) : rgb(214, 206, 190);
        g.fillRect(xx, yy, 10, 10);
        e.fillStyle = on ? rgb(120, 30, 20) : rgb(180, 150, 110);
        e.fillRect(xx, yy, 10, 10);
      }
    }
    g.fillStyle = rgb(180, 180, 176);
    e.fillStyle = rgb(190, 180, 150);
    for (const c of [g, e]) c.fillRect(x + S - 70, cy - 46, 34, 44);
    g.fillStyle = rgb(170, 140, 70);
    g.fillRect(x, y + 30, S, 4);
    g.fillStyle = rgb(60, 40, 30);
    g.fillRect(x, y + S - 40, S, 40);
  });

  const map = new THREE.CanvasTexture(mc);
  map.colorSpace = THREE.SRGBColorSpace;
  map.anisotropy = 8;
  const emissive = new THREE.CanvasTexture(ec);
  emissive.colorSpace = THREE.SRGBColorSpace;
  return { map, emissive, means: tileMeans(mc) };
}

// ------------------------------------------------------------------------------------- ground

export interface GroundTextures {
  asphalt: THREE.Texture;
  asphaltRough: THREE.Texture;
  setts: THREE.Texture;
  settsRough: THREE.Texture;
  paving: THREE.Texture;
  yard: THREE.Texture;
  grass: THREE.Texture;
  gravel: THREE.Texture;
  /** The baked set only (null on the canvas path): roughness for the flags and yards, tangent-space normals for
   *  the setts and flags (HIGH), the granite of the kerb and the wear mask of the road paint. */
  pavingRough: THREE.Texture | null;
  yardRough: THREE.Texture | null;
  settsNormal: THREE.Texture | null;
  pavingNormal: THREE.Texture | null;
  kerb: THREE.Texture | null;
  paintWear: THREE.Texture | null;
  /** The baked set only: the kerb-face sheet (materials.ts kerb shader): the face strip with the gutter at its
   *  foot in v 0 … 0.5 (7.2 m along, world-space), the gully gratings above it (streetLife, by their own uv). */
  kerbFace: THREE.Texture | null;
  /** Tileable ripple normal for the canal, basin and puddles (7 m repeat set by materials.ts). */
  waterNormal: THREE.Texture;
  /** Soft alpha (green channel) for the puddle quads, 0 at their edge. */
  puddleMask: THREE.Texture;
  /** The baked set: resolves to the loaded textures (or the painted ones when the files are missing). */
  ready?: Promise<GroundTextures>;
}

/**
 * Layout of the kerb-face sheet (ground_bake.py kerbface): the face strip takes v 0 … 0.5 over `along` metres of
 * kerb and the face's `height`; the gutter strip (the road's 0.3 m along the kerb, kerb side first) v `gutter`;
 * the two cast-iron gully gratings (0.7 × 0.32 m) sit above at `grates` (u0, v0, u1, v1). materials.ts samples
 * the strip for the kerb's face and the gutter for a quad flagged with a uv of 4 + (·, 0 … 1 across); streetLife
 * draws the gratings with `2 + uv`.
 */
export const KERB_FACE = {
  along: 7.2,
  height: 0.15,
  gutter: [0.512, 0.55] as [number, number],
  grates: [
    [0.02, 0.5556, 0.2631, 1.0],
    [0.2778, 0.5556, 0.5208, 1.0],
  ] as [number, number, number, number][],
};

/**
 * The Blender-baked ground textures from public/textures/ground-*.ktx2 (the default; `?canvasground` paints the
 * canvases at boot as before, for A/B): docs/PERF.md "Blender ground surfaces", art/models/blender/ground_bake.py,
 * scripts/build-ground-textures.mjs. The same repeats as the painter (materials.ts groundMat()), so the builders and
 * their world-space uvs are untouched; compressed in GPU memory where the canvases cost 12 MB with mips.
 */
export const BAKED_GROUND = typeof location === 'undefined' || !new URLSearchParams(location.search).has('canvasground');

function repeatTex(c: HTMLCanvasElement, srgb = true): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  if (srgb) t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

/** A 4 × 4 flat texture that repeats: stands in for a ground texture until its KTX2 file has loaded. */
function repeatPlaceholder(r: number, g: number, b: number, srgb: boolean): THREE.DataTexture {
  const d = new Uint8Array(4 * 4 * 4);
  for (let i = 0; i < d.length; i += 4) d.set([r, g, b, 255], i);
  const t = new THREE.DataTexture(d, 4, 4);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  if (srgb) t.colorSpace = THREE.SRGBColorSpace;
  t.needsUpdate = true;
  return t;
}

/** The baked set (see BAKED_GROUND): flat placeholders now, the KTX2 files (or the painter) through `ready`. */
export function createGroundTexturesBaked(): GroundTextures {
  const ph = (r: number, g: number, b: number): THREE.Texture => repeatPlaceholder(r, g, b, true);
  const data = (r: number, g: number, b: number): THREE.Texture => repeatPlaceholder(r, g, b, false);
  const set: GroundTextures = {
    asphalt: ph(74, 74, 77),
    asphaltRough: data(0, 178, 0),
    setts: ph(82, 80, 78),
    settsRough: data(0, 200, 0),
    paving: ph(108, 105, 99),
    pavingRough: data(0, 200, 0),
    yard: ph(88, 86, 82),
    yardRough: data(0, 220, 0),
    settsNormal: data(128, 128, 255),
    pavingNormal: data(128, 128, 255),
    kerb: ph(142, 140, 136),
    kerbFace: ph(142, 140, 136),
    paintWear: data(220, 220, 220),
    grass: ph(53, 64, 32),
    gravel: ph(116, 104, 81),
    // flat water and no puddles until the files are in
    waterNormal: data(128, 128, 255),
    puddleMask: data(0, 0, 0),
  };
  const BASE_URL = import.meta.env?.BASE_URL ?? './';
  set.ready = (async (): Promise<GroundTextures> => {
    try {
      const loader = ktx2Loader();
      const load = async (name: string, srgb: boolean, wrap = true): Promise<THREE.Texture> => {
        const t = await loader.loadAsync(`${BASE_URL}textures/ground-${name}.ktx2`);
        t.wrapS = t.wrapT = wrap ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
        t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
        t.anisotropy = 8;
        t.needsUpdate = true;
        return t;
      };
      const [asphalt, asphaltRough, setts, settsRough, settsNormal, paving, pavingRough, pavingNormal, yard, yardRough, kerb, paintWear, grass, gravel, kerbFace, waterNormal, puddleMask] = await Promise.all([
        load('asphalt', true),
        load('asphalt-rough', false),
        load('setts', true),
        load('setts-rough', false),
        load('setts-normal', false),
        load('paving', true),
        load('paving-rough', false),
        load('paving-normal', false),
        load('yard', true),
        load('yard-rough', false),
        load('kerb', true),
        load('paintwear-rough', false),
        load('grass', true),
        load('gravel', true),
        load('kerbface', true),
        load('water-normal', false),
        load('puddle-mask', false, false),
      ]);
      // the sheet repeats along the kerb only; its v is the face strip below, the gratings above
      kerbFace.wrapT = THREE.ClampToEdgeWrapping;
      return { asphalt, asphaltRough, setts, settsRough, settsNormal, paving, pavingRough, pavingNormal, yard, yardRough, kerb, paintWear, grass, gravel, kerbFace, waterNormal, puddleMask };
    } catch {
      // no files (or no KTX2 support): the painter as before
      return createGroundTextures();
    }
  })();
  return set;
}

/** Wet roughness: mostly damp, with glossy puddles (roughness in the G channel). */
function wetRoughness(rng: Rng, size: number, base: number, puddles: number): HTMLCanvasElement {
  const [c, g] = canvas(size, size);
  g.fillStyle = rgb(0, base, 0);
  g.fillRect(0, 0, size, size);
  for (let i = 0; i < 1600; i++) {
    g.fillStyle = rgb(0, base + rng.range(-40, 40), 0, 0.5);
    g.fillRect(rng.next() * size, rng.next() * size, 3, 3);
  }
  for (let i = 0; i < puddles; i++) {
    const x = rng.next() * size;
    const y = rng.next() * size;
    const r = rng.range(size * 0.03, size * 0.12);
    for (const [ox, oy] of [
      [0, 0],
      [size, 0],
      [-size, 0],
      [0, size],
      [0, -size],
    ] as const) {
      const grad = g.createRadialGradient(x + ox, y + oy, 0, x + ox, y + oy, r);
      grad.addColorStop(0, rgb(0, 48, 0, 0.9));
      grad.addColorStop(0.6, rgb(0, 70, 0, 0.6));
      grad.addColorStop(1, rgb(0, base, 0, 0));
      g.fillStyle = grad;
      g.beginPath();
      g.ellipse(x + ox, y + oy, r * 1.6, r, rng.range(0, 3), 0, Math.PI * 2);
      g.fill();
    }
  }
  return c;
}

export function createGroundTextures(): GroundTextures {
  const rng = new Rng(777);
  // Asphalt (16 m repeat)
  const [ac, a] = canvas(1024, 1024);
  a.fillStyle = rgb(70, 71, 76);
  a.fillRect(0, 0, 1024, 1024);
  // batches laid in different years: broad patches a shade apart, then the aggregate on top (kept faint: at
  // 0.6 alpha the darker ellipses read as soft round shadows of nothing on a sunlit road)
  for (let i = 0; i < 10; i++) {
    const pv = rng.range(60, 80);
    a.fillStyle = rgb(pv, pv, pv + 3, 0.38);
    a.beginPath();
    a.ellipse(rng.next() * 1024, rng.next() * 1024, rng.range(120, 360), rng.range(80, 260), rng.range(0, 3), 0, Math.PI * 2);
    a.fill();
  }
  speckle(a, rng, 0, 0, 1024, 1024, 60000, rgb(96, 96, 100, 0.6), rgb(44, 44, 48, 0.6), 2);
  // coarse chippings that catch the light
  speckle(a, rng, 0, 0, 1024, 1024, 9000, rgb(128, 126, 122, 0.5), rgb(30, 30, 34, 0.5), 2.6);
  // lane-width relays a shade apart; faint, so they read as wear and not as a grid of rectangles
  for (let i = 0; i < 14; i++) {
    const pv = rng.range(58, 80);
    a.fillStyle = rgb(pv, pv, pv + 4, 0.28);
    a.fillRect(rng.next() * 1024, rng.next() * 1024, rng.range(40, 260), rng.range(40, 200));
  }
  // old patches a shade off the surround, with a faint sealed edge
  for (let i = 0; i < 5; i++) {
    const x = rng.next() * 1024;
    const y = rng.next() * 1024;
    const w = rng.range(60, 200);
    const h = rng.range(40, 140);
    a.fillStyle = rgb(44, 44, 48, 0.25);
    a.fillRect(x - 2, y - 2, w + 4, h + 4);
    const pv = rng.range(64, 78);
    a.fillStyle = rgb(pv, pv, pv + 4, 0.8);
    a.fillRect(x, y, w, h);
  }
  a.strokeStyle = rgb(34, 34, 37, 0.6);
  a.lineWidth = 1.2;
  for (let i = 0; i < 18; i++) {
    let x = rng.next() * 1024;
    let y = rng.next() * 1024;
    a.beginPath();
    a.moveTo(x, y);
    for (let k = 0; k < 8; k++) {
      x += rng.range(-30, 30);
      y += rng.range(-30, 30);
      a.lineTo(x, y);
    }
    a.stroke();
  }
  // tar-sealed repairs: short wobbly runs, not full-width seams (those read as wires from above)
  a.strokeStyle = rgb(30, 30, 33, 0.55);
  a.lineWidth = 3;
  for (let i = 0; i < 9; i++) {
    let x = rng.next() * 1024;
    let y = rng.next() * 1024;
    a.beginPath();
    a.moveTo(x, y);
    for (let k = 0; k < 5; k++) {
      x += rng.range(-18, 18);
      y += rng.range(8, 26);
      a.lineTo(x, y);
    }
    a.stroke();
  }
  // oil stains
  for (let i = 0; i < 16; i++) {
    const grad = a.createRadialGradient(0, 0, 0, 0, 0, 1);
    void grad;
    a.fillStyle = rgb(14, 14, 16, rng.range(0.2, 0.45));
    a.beginPath();
    a.ellipse(rng.next() * 1024, rng.next() * 1024, rng.range(8, 30), rng.range(5, 18), rng.range(0, 3), 0, Math.PI * 2);
    a.fill();
  }

  // Granite setts (4 m repeat)
  const [sc, s] = canvas(512, 512);
  // silt and sand in the joints, a shade warmer than the stone
  s.fillStyle = rgb(36, 33, 30);
  s.fillRect(0, 0, 512, 512);
  speckle(s, rng, 0, 0, 512, 512, 3000, rgb(70, 62, 50, 0.5), rgb(20, 18, 16, 0.5), 2);
  const rowH = 512 / 40;
  for (let row = 0; row < 40; row++) {
    const off = row % 2 ? 13 : 0;
    for (let col = -1; col < 22; col++) {
      const x = col * 25.6 + off;
      if (rng.chance(0.015)) {
        // a stone gone, tar poured in
        s.fillStyle = rgb(24, 24, 26);
        s.fillRect(x + 1, row * rowH + 1, 23.6, rowH - 2);
        continue;
      }
      const t = rng.range(-14, 14);
      const warm = rng.range(-6, 6);
      const worn = rng.chance(0.2) ? 10 : 0;
      s.fillStyle = rgb(76 + t + warm + worn, 76 + t + worn, 80 + t - warm + worn);
      s.beginPath();
      s.roundRect(x + 1.5, row * rowH + 1.5, 22.6, rowH - 3, 3);
      s.fill();
      // crowned top: a light catch on the upper edge, a shadow on the lower
      s.fillStyle = rgb(255, 255, 255, 0.08);
      s.fillRect(x + 3, row * rowH + 2, 18, 2);
      s.fillStyle = rgb(0, 0, 0, 0.2);
      s.fillRect(x + 3, row * rowH + rowH - 4, 18, 2);
    }
  }
  // the polished crown where the wheels run, and oily grime
  for (let i = 0; i < 6; i++) {
    s.fillStyle = rgb(20, 20, 22, rng.range(0.1, 0.25));
    s.beginPath();
    s.ellipse(rng.next() * 512, rng.next() * 512, rng.range(20, 60), rng.range(10, 30), rng.range(0, 3), 0, Math.PI * 2);
    s.fill();
  }

  // Paving flags (6 m repeat): 0.9 x 0.6 m slabs
  const [pc, p] = canvas(512, 512);
  p.fillStyle = rgb(70, 70, 70);
  p.fillRect(0, 0, 512, 512);
  const fw = 512 / 6.6;
  const fh = 512 / 10;
  for (let row = 0; row < 10; row++) {
    const off = row % 2 ? fw / 2 : 0;
    for (let col = -1; col < 8; col++) {
      const t = rng.range(-10, 10);
      const x0 = col * fw + off + 1;
      const y0 = row * fh + 1;
      p.fillStyle = rgb(118 + t, 116 + t, 110 + t);
      p.fillRect(x0, y0, fw - 2, fh - 2);
      // a flag that rocks has a dark damp edge; a few are a different batch
      if (rng.chance(0.1)) {
        p.fillStyle = rgb(60, 60, 58, 0.32);
        p.fillRect(x0, y0, fw - 2, fh - 2);
      } else if (rng.chance(0.1)) {
        p.fillStyle = rgb(128, 120, 104, 0.6);
        p.fillRect(x0, y0, fw - 2, fh - 2);
      }
      // cracked flag
      if (rng.chance(0.14)) {
        p.strokeStyle = rgb(40, 40, 38, 0.8);
        p.lineWidth = 1.2;
        p.beginPath();
        let cx = x0 + rng.range(4, fw - 6);
        let cy = y0;
        p.moveTo(cx, cy);
        while (cy < y0 + fh - 3) {
          cx += rng.range(-5, 5);
          cy += rng.range(6, 12);
          p.lineTo(cx, cy);
        }
        p.stroke();
      }
      // worn, lit upper edge and a shadow in the joint below
      p.fillStyle = rgb(255, 255, 255, 0.06);
      p.fillRect(x0, y0, fw - 2, 2);
      p.fillStyle = rgb(0, 0, 0, 0.22);
      p.fillRect(x0, y0 + fh - 4, fw - 2, 2);
    }
  }
  // the odd flag lifted and the hole filled with tarmac
  {
    const col = rng.int(0, 5);
    const row = rng.int(0, 8);
    p.fillStyle = rgb(72, 72, 74);
    p.fillRect(col * fw + 1, row * fh + 1, fw - 2, fh * rng.range(1, 2) - 2);
  }
  speckle(p, rng, 0, 0, 512, 512, 8000, rgb(140, 138, 132, 0.4), rgb(60, 58, 56, 0.4), 1.6);
  for (let i = 0; i < 20; i++) {
    p.fillStyle = rgb(30, 30, 30, rng.range(0.05, 0.2));
    p.beginPath();
    p.ellipse(rng.next() * 512, rng.next() * 512, rng.range(4, 14), rng.range(3, 10), 0, 0, Math.PI * 2);
    p.fill();
  }
  // trodden-in chewing gum
  for (let i = 0; i < 40; i++) {
    p.fillStyle = rgb(70, 68, 66, rng.range(0.5, 0.8));
    p.beginPath();
    p.arc(rng.next() * 512, rng.next() * 512, rng.range(1.2, 2.4), 0, Math.PI * 2);
    p.fill();
  }

  // Yards: cinders / worn concrete / mud (12 m repeat)
  const [yc, y] = canvas(512, 512);
  y.fillStyle = rgb(58, 54, 50);
  y.fillRect(0, 0, 512, 512);
  speckle(y, rng, 0, 0, 512, 512, 30000, rgb(80, 76, 70, 0.5), rgb(36, 34, 32, 0.5), 2.5);
  // packed cinders vary in tone, not hue: the channels move together, with a little brick dust (warm)
  // or wet ash (cool) on top, so the yard reads as one surface and not a camouflage pattern
  for (let i = 0; i < 40; i++) {
    const l = rng.range(42, 76);
    const warm = rng.range(-4, 5);
    y.fillStyle = rgb(l + warm, l - 2, l - 6 - warm * 0.6, rng.range(0.15, 0.4));
    y.beginPath();
    y.ellipse(rng.next() * 512, rng.next() * 512, rng.range(20, 90), rng.range(10, 60), rng.range(0, 3), 0, Math.PI * 2);
    y.fill();
  }
  // cinders and clinker, the odd rust stain from scrap left lying, oil
  speckle(y, rng, 0, 0, 512, 512, 3000, rgb(20, 18, 18, 0.7), rgb(96, 90, 84, 0.5), 3.5);
  for (let i = 0; i < 4; i++) {
    y.fillStyle = rgb(100, 56, 32, rng.range(0.08, 0.16));
    y.beginPath();
    y.ellipse(rng.next() * 512, rng.next() * 512, rng.range(8, 24), rng.range(6, 16), rng.range(0, 3), 0, Math.PI * 2);
    y.fill();
  }
  for (let i = 0; i < 4; i++) {
    y.fillStyle = rgb(16, 16, 18, rng.range(0.15, 0.3));
    y.beginPath();
    y.ellipse(rng.next() * 512, rng.next() * 512, rng.range(10, 30), rng.range(6, 18), rng.range(0, 3), 0, Math.PI * 2);
    y.fill();
  }

  const { grass, gravel } = createGrassAndGravel(rng);
  return {
    asphalt: repeatTex(ac),
    asphaltRough: repeatTex(wetRoughness(rng, 512, 150, 18), false),
    setts: repeatTex(sc),
    settsRough: repeatTex(wetRoughness(rng, 256, 120, 6), false),
    paving: repeatTex(pc),
    yard: repeatTex(yc),
    grass,
    gravel,
    pavingRough: null,
    yardRough: null,
    settsNormal: null,
    pavingNormal: null,
    kerb: null,
    paintWear: null,
    kerbFace: null,
    waterNormal: createWaterNormal(),
    puddleMask: createPuddleMask(),
  };
}

/** Grass and the park gravel, painted (`?canvasground`; the baked set has them from the bake too). */
function createGrassAndGravel(rng = new Rng(778)): { grass: THREE.CanvasTexture; gravel: THREE.CanvasTexture } {
  // Grass (8 m repeat)
  const [gc, gr] = canvas(512, 512);
  gr.fillStyle = rgb(44, 58, 34);
  gr.fillRect(0, 0, 512, 512);
  // the parks department mows in stripes (8 m repeat = four 2 m passes)
  // Kept faint and feathered: hard-edged ±5% bands read as a striped carpet across a whole square.
  for (let band = 0; band < 4; band++) {
    const x0 = band * 128;
    const sweep = gr.createLinearGradient(x0, 0, x0 + 128, 0);
    const tone = band % 2 ? '255,255,230' : '0,0,0';
    const peak = band % 2 ? 0.026 : 0.032;
    sweep.addColorStop(0, `rgba(${tone},0)`);
    sweep.addColorStop(0.22, `rgba(${tone},${peak})`);
    sweep.addColorStop(0.78, `rgba(${tone},${peak})`);
    sweep.addColorStop(1, `rgba(${tone},0)`);
    gr.fillStyle = sweep;
    gr.fillRect(x0, 0, 128, 512);
  }
  speckle(gr, rng, 0, 0, 512, 512, 40000, rgb(64, 84, 44, 0.6), rgb(30, 40, 24, 0.6), 2.2);
  for (let i = 0; i < 30; i++) {
    gr.fillStyle = rgb(rng.range(40, 70), rng.range(50, 80), rng.range(28, 40), rng.range(0.15, 0.35));
    gr.beginPath();
    gr.ellipse(rng.next() * 512, rng.next() * 512, rng.range(20, 70), rng.range(16, 50), 0, 0, Math.PI * 2);
    gr.fill();
  }
  // worn to earth where people cut the corner, clover and plantain in darker patches
  for (let i = 0; i < 5; i++) {
    gr.fillStyle = rgb(78, 66, 46, rng.range(0.25, 0.45));
    gr.beginPath();
    gr.ellipse(rng.next() * 512, rng.next() * 512, rng.range(14, 36), rng.range(8, 20), rng.range(0, 3), 0, Math.PI * 2);
    gr.fill();
  }
  for (let i = 0; i < 12; i++) {
    gr.fillStyle = rgb(30, 52, 30, rng.range(0.2, 0.4));
    gr.beginPath();
    gr.ellipse(rng.next() * 512, rng.next() * 512, rng.range(8, 24), rng.range(6, 16), 0, 0, Math.PI * 2);
    gr.fill();
  }

  // Gravel path
  const [vc, v] = canvas(256, 256);
  v.fillStyle = rgb(110, 100, 84);
  v.fillRect(0, 0, 256, 256);
  speckle(v, rng, 0, 0, 256, 256, 12000, rgb(140, 130, 112, 0.6), rgb(70, 64, 54, 0.6), 2);
  return { grass: repeatTex(gc), gravel: repeatTex(vc) };
}

// ------------------------------------------------------------------------------------- misc

/** Soft radial glow (lamp pools, light streaks). */
export function createGlowTexture(): THREE.CanvasTexture {
  const [c, g] = canvas(128, 128);
  const grad = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.25, 'rgba(255,255,255,0.55)');
  grad.addColorStop(0.6, 'rgba(255,255,255,0.15)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/**
 * Puddle alpha mask (the canvas path; the baked set loads ground-puddle-mask.ktx2): grey levels in RGB on an
 * opaque canvas, because three's `alphaMap` reads the green channel (the glow texture keeps its falloff in
 * alpha, so as an alphaMap it gave hard-edged discs). A solid middle feathering out over a lobed, irregular
 * rim so standing water has no circle outline. Linear data (no colour space).
 */
function createPuddleMask(): THREE.CanvasTexture {
  const size = 128;
  const [c, g] = canvas(size, size);
  const img = g.createImageData(size, size);
  const rng = new Rng(4242);
  const lobes: [number, number, number][] = [];
  for (let k = 2; k <= 5; k++) lobes.push([k, rng.range(0, Math.PI * 2), rng.range(0.03, 0.07) * (k <= 3 ? 1.4 : 1)]);
  for (let yy = 0; yy < size; yy++) {
    for (let xx = 0; xx < size; xx++) {
      const dx = (xx + 0.5) / size - 0.5;
      const dy = (yy + 0.5) / size - 0.5;
      const r = Math.hypot(dx, dy) * 2;
      const a = Math.atan2(dy, dx);
      let rim = 0.86;
      for (const [k, ph, amp] of lobes) rim += Math.sin(a * k + ph) * amp;
      // smoothstep from the rim inwards over a third of the radius
      const t = Math.min(1, Math.max(0, (rim - r) / 0.34));
      const v = Math.round(255 * t * t * (3 - 2 * t));
      const o = (yy * size + xx) * 4;
      img.data[o] = v;
      img.data[o + 1] = v;
      img.data[o + 2] = v;
      img.data[o + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  return new THREE.CanvasTexture(c);
}

/** Tileable ripple normal map for the canal and basin (the canvas path; the baked set loads ground-water-normal.ktx2). */
function createWaterNormal(): THREE.CanvasTexture {
  const size = 256;
  const [c, g] = canvas(size, size);
  const img = g.createImageData(size, size);
  const rng = new Rng(99);
  const waves: [number, number, number, number][] = [];
  // fourteen plane waves over a wider band of wavenumbers, the short ones weak: with nine of similar
  // strength the sum was a lattice of diamonds under every lamp
  for (let i = 0; i < 14; i++) {
    const kx = rng.int(1, 8);
    const ky = rng.int(-8, 8);
    const amp = rng.range(0.25, 1) * (kx * kx + ky * ky > 36 ? 0.4 : 1);
    waves.push([kx, ky, rng.range(0, Math.PI * 2), amp]);
  }
  for (let yy = 0; yy < size; yy++) {
    for (let xx = 0; xx < size; xx++) {
      let dx = 0;
      let dy = 0;
      for (const [kx, ky, ph, amp] of waves) {
        const a = ((kx * xx + ky * yy) / size) * Math.PI * 2 + ph;
        const d = Math.cos(a) * amp;
        dx += d * kx;
        dy += d * ky;
      }
      const i = (yy * size + xx) * 4;
      img.data[i] = 128 + Math.max(-127, Math.min(127, dx * 6));
      img.data[i + 1] = 128 + Math.max(-127, Math.min(127, dy * 6));
      img.data[i + 2] = 255;
      img.data[i + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

// ------------------------------------------------------------------------------------- signage

export type SignStyle = 'fascia' | 'plate' | 'painted' | 'billboard' | 'pubBoard' | 'neon' | 'police' | 'hanging' | 'poster' | 'graffiti' | 'busStop' | 'timetable' | 'aboard';

export interface SignAtlas {
  texture: THREE.CanvasTexture;
  /** Paints a sign and returns its UV rect [u0, v0, u1, v1] and aspect (w/h). */
  add(text: string, style: SignStyle, seed?: number): { uv: [number, number, number, number]; aspect: number };
}

/**
 * The Blender-baked plates under the signs (art/models/blender/sign_bake.py → public/textures/sign-plates.webp,
 * src/world/view/signPlates.json): the moulded fascia frames, the pub board, enamelled plates, the hoarding and
 * fly-poster paper, the club blade's backing, the hanging sign with its bracket and emblem, the bus stop flag.
 * They are drawn into the sign atlas under the lettering (same canvas, same material, no extra texture), and
 * the boards / paper are tinted per sign with a multiply over the cell's `inner` rect. `?canvassigns` paints
 * the plates on the canvas as before (an A/B); a plate that fails to load leaves the painted fallback.
 */
export const SIGN_PLATES = typeof location === 'undefined' || !new URLSearchParams(location.search).has('canvassigns');
type PlateCell = { x: number; y: number; w: number; h: number; inner?: number[] };
const PLATES = signPlates as { size: number[]; cells: Record<string, PlateCell> };

export function createSignAtlas(): SignAtlas {
  const W = 2048;
  const H = 2048;
  const [c, g] = canvas(W, H);
  g.clearRect(0, 0, W, H);
  let shelfY = 0;
  let shelfH = 0;
  let cursorX = 0;
  const cache = new Map<string, { uv: [number, number, number, number]; aspect: number }>();
  const alloc = (w: number, h: number): [number, number] | null => {
    if (cursorX + w > W) {
      shelfY += shelfH + 2;
      shelfH = 0;
      cursorX = 0;
    }
    if (shelfY + h > H) return null;
    const pos: [number, number] = [cursorX, shelfY];
    cursorX += w + 2;
    shelfH = Math.max(shelfH, h);
    return pos;
  };
  /**
   * The largest of maxH, maxH − 2, … (down to 8) at which `text` fits in maxW. Starts from the width-proportional
   * guess and steps from there (two or three measures instead of one per 2 px); the same answer as stepping
   * down from maxH, since the width grows with the size.
   */
  const fitFont = (text: string, maxW: number, maxH: number, family: string, weight = 'bold'): number => {
    // step k is maxH with 2 taken off k times (the same float the step-down loop reached)
    const at = (k: number): number => {
      let sz = maxH;
      for (let i = 0; i < k; i++) sz -= 2;
      return sz;
    };
    const fits = (k: number): boolean => {
      g.font = `${weight} ${at(k)}px ${family}`;
      return g.measureText(text).width <= maxW;
    };
    if (maxH <= 8 || fits(0)) {
      g.font = `${weight} ${maxH}px ${family}`;
      return maxH;
    }
    const w0 = g.measureText(text).width;
    // the last step the loop can reach: the first size <= 8
    const last = Math.ceil((maxH - 8) / 2);
    let k = Math.min(last, Math.max(1, Math.ceil((maxH - (maxH * maxW) / w0) / 2)));
    // The width is proportional to the size, to well under 2 %: a step whose proportional width is clear of maxW
    // by more than that needs no measuring (most signs take one measure, a borderline one two or three).
    const guess = (j: number): number => (w0 * at(j)) / maxH;
    while (k < last && (guess(k) > maxW * 1.02 || (guess(k) > maxW * 0.98 && !fits(k)))) k++;
    while (k > 1 && guess(k - 1) <= maxW * 1.02 && (guess(k - 1) < maxW * 0.98 || fits(k - 1))) k--;
    g.font = `${weight} ${at(k)}px ${family}`;
    return at(k);
  };
  const texture = new THREE.CanvasTexture(c);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  // Period type: a sign-writer's serif (Georgia carries the bracketed slabs of a Clarendon), the 1970 shop
  // fascia's grotesque (Helvetica / Arial; narrowed a touch by a scale, the way a vinyl-letter fascia was set),
  // and a script for neon (Snell on the iPhone, Brush Script on Windows).
  const SERIF = "Georgia, 'Times New Roman', serif";
  const SANS = "'Helvetica Neue', Helvetica, Arial, sans-serif";
  const SCRIPT = "'Snell Roundhand', 'Brush Script MT', 'Segoe Script', 'Lucida Handwriting', cursive";
  const boardColours = ['#1f3a2c', '#4a1c1c', '#1c2a44', '#2a2420', '#3d2a14', '#14302e'];
  const dims: Record<SignStyle, [number, number]> = {
    fascia: [512, 72],
    plate: [256, 52],
    painted: [768, 110],
    billboard: [384, 192],
    pubBoard: [512, 80],
    neon: [128, 384],
    police: [256, 64],
    hanging: [96, 128],
    poster: [128, 176],
    graffiti: [256, 96],
    busStop: [96, 112],
    timetable: [64, 96],
    aboard: [96, 128],
  };

  type Entry = { x: number; y: number; w: number; h: number; text: string; style: SignStyle; seed: number };
  const entries: Entry[] = [];
  let plates: HTMLImageElement | null = null;

  /** Gilt sign-writer's lettering: a dark drop shade, a thin outline, a gold face shaded top to bottom. */
  const gilt = (text: string, cx: number, cy: number, size: number, fam: string, shade = '#2a1c08'): void => {
    g.font = `bold ${size}px ${fam}`;
    g.fillStyle = shade;
    g.fillText(text, cx + size * 0.04, cy + size * 0.06);
    const grad = g.createLinearGradient(0, cy - size * 0.5, 0, cy + size * 0.5);
    grad.addColorStop(0, '#f8e7a8');
    grad.addColorStop(0.45, '#e2bc55');
    grad.addColorStop(0.55, '#c9a040');
    grad.addColorStop(1, '#8f6c22');
    g.lineWidth = Math.max(1, size * 0.04);
    g.strokeStyle = '#4d3510';
    g.strokeText(text, cx, cy);
    g.fillStyle = grad;
    g.fillText(text, cx, cy);
  };

  /** A neon tube: the glow halo (twice, so it builds), the tube in its colour, a near-white core down the middle. */
  const tube = (draw: () => void, colour: string, core: string, width: number): void => {
    g.lineJoin = 'round';
    g.lineCap = 'round';
    g.shadowColor = colour;
    g.shadowBlur = width * 2.6;
    g.strokeStyle = colour;
    g.lineWidth = width;
    draw();
    draw();
    g.shadowBlur = 0;
    g.shadowColor = 'transparent';
    g.strokeStyle = core;
    g.lineWidth = width * 0.42;
    draw();
  };

  const paint = (en: Entry): void => {
    const { x, y, w, h, text, style, seed } = en;
    g.save();
    g.beginPath();
    g.rect(x, y, w, h);
    g.clip();
    g.clearRect(x, y, w, h);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    const hash = [...text].reduce((a, ch) => (a * 31 + ch.charCodeAt(0)) >>> 0, seed);
    /** Draws the baked plate `name` under the cell; null when the plates are not (yet) there. */
    const plate = (name: string): PlateCell | null => {
      const cell = plates ? PLATES.cells[name] : undefined;
      if (!plates || !cell) return null;
      g.drawImage(plates, cell.x, cell.y, cell.w, cell.h, x, y, w, h);
      return cell;
    };
    /** Tints the plate's board / paper (its `inner` rect) with a colour: a multiply, then the plate's own alpha back. */
    const tint = (cell: PlateCell, colour: string): void => {
      if (!plates) return;
      const [ix = 0, iy = 0, iw = cell.w, ih = cell.h] = cell.inner ?? [];
      g.globalCompositeOperation = 'multiply';
      g.fillStyle = colour;
      g.fillRect(x + ix, y + iy, iw, ih);
      g.globalCompositeOperation = 'destination-in';
      g.drawImage(plates, cell.x, cell.y, cell.w, cell.h, x, y, w, h);
      g.globalCompositeOperation = 'source-over';
    };
    if (style === 'fascia') {
      const look = hash % 3; // 0: the shop's grotesque on a painted board, 1: gilt serif, 2: block capitals on cream
      const cream = look === 2;
      const board = cream ? '#ddd3b6' : boardColours[(hash >>> 3) % boardColours.length]!;
      const cell = plate(look === 1 ? 'fasciaGilt' : 'fasciaPlain');
      if (cell) tint(cell, cream ? '#f4ecd6' : board); // the baked board is already a warm grey: the cream goes on lighter
      else {
        g.fillStyle = board;
        g.fillRect(x, y, w, h);
        g.fillStyle = cream ? 'rgba(80,60,40,0.15)' : 'rgba(255,255,255,0.05)';
        for (let i = 0; i < 6; i++) g.fillRect(x + ((hash >> i) % w), y, 2, h);
        g.strokeStyle = look === 1 ? '#c9a24a' : cream ? '#3a2a1c' : 'rgba(230,210,160,0.7)';
        g.lineWidth = 2;
        g.strokeRect(x + 5, y + 5, w - 10, h - 10);
      }
      const label = text.toUpperCase();
      if (look === 1) {
        const size = fitFont(label, w - 44, h * 0.56, SERIF);
        gilt(label, x + w / 2, y + h / 2 + 2, size, SERIF);
      } else {
        // 1970 fascia: grotesque capitals, a little narrowed, a shade behind the letters
        g.translate(x + w / 2, y + h / 2 + 1);
        g.scale(0.92, 1);
        const size = fitFont(label, (w - 40) / 0.92, h * 0.6, SANS);
        g.font = `bold ${size}px ${SANS}`;
        if (cream) {
          g.fillStyle = '#8a3a22';
          g.fillText(label, 2, 3);
          g.fillStyle = '#1e2a3a';
        } else {
          g.fillStyle = 'rgba(0,0,0,0.35)';
          g.fillText(label, 1, 2);
          g.fillStyle = '#efe6cf';
        }
        g.fillText(label, 0, 0);
      }
    } else if (style === 'pubBoard') {
      if (!plate('pubBoard')) {
        g.fillStyle = '#101512';
        g.fillRect(x, y, w, h);
        g.strokeStyle = '#c9a24a';
        g.lineWidth = 3;
        g.strokeRect(x + 4, y + 4, w - 8, h - 8);
      }
      const size = fitFont(text, w - 60, h * 0.56, SERIF);
      gilt(text, x + w / 2, y + h / 2 + 2, size, SERIF, '#070503');
    } else if (style === 'poster') {
      // Fly-poster: "HEADLINE · line · line", printed on cheap coloured stock, edges torn.
      const parts = text.split(' · ');
      const bg = ['#e8dcc0', '#d8b23c', '#c8452c', '#efe7d2', '#3f6a8a', '#e0e0d6'][hash % 6]!;
      const dark = bg === '#c8452c' || bg === '#3f6a8a';
      const cell = plate(hash & 8 ? 'posterB' : 'posterA');
      if (cell) tint(cell, bg);
      else {
        g.fillStyle = bg;
        g.beginPath();
        g.moveTo(x + 2, y + 3);
        for (let xx = 0; xx <= w; xx += 12) g.lineTo(x + xx, y + 2 + ((hash >> (xx % 7)) & 3));
        g.lineTo(x + w - 2, y + h - 4);
        for (let xx = w; xx >= 0; xx -= 11) g.lineTo(x + xx, y + h - 2 - ((hash >> (xx % 5)) & 5));
        g.closePath();
        g.fill();
      }
      g.fillStyle = dark ? '#f2ead6' : '#1a1410';
      let size = fitFont(parts[0] ?? text, w - 22, h * 0.22, SANS);
      g.font = `bold ${size}px ${SANS}`;
      g.fillText(parts[0] ?? text, x + w / 2, y + h * 0.22);
      g.fillStyle = dark ? '#f2ead6' : '#9b1c1c';
      g.fillRect(x + 14, y + h * 0.35, w - 28, 3);
      for (let i = 1; i < parts.length; i++) {
        g.fillStyle = dark ? '#f2ead6' : i === 1 ? '#9b1c1c' : '#1a1410';
        size = fitFont(parts[i]!, w - 24, h * (i === 1 ? 0.15 : 0.1), SERIF);
        g.font = `bold ${size}px ${SERIF}`;
        g.fillText(parts[i]!, x + w / 2, y + h * (0.33 + i * 0.16));
      }
      if (!cell) {
        // weathering: a damp stain and a torn corner
        g.fillStyle = 'rgba(40,30,20,0.18)';
        g.fillRect(x, y + h * 0.72, w, h * 0.28);
        g.clearRect(x + w - 22, y + h - 30, 22, 30);
      }
    } else if (style === 'graffiti') {
      // Spray-painted tag on brick: drips, uneven letters, no background (alpha-tested).
      const cols = ['#e9e4d8', '#c8342a', '#121212', '#e0c040', '#4a7ad0'];
      const c0 = cols[hash % cols.length]!;
      const fam = "Impact, 'Arial Black', sans-serif";
      const size = fitFont(text, w - 24, h * 0.62, fam, 'bold');
      g.font = `bold ${size}px ${fam}`;
      g.translate(x + w / 2, y + h / 2);
      g.rotate(((hash % 9) - 4) * 0.02);
      g.lineWidth = 5;
      g.strokeStyle = c0 === '#121212' ? '#e9e4d8' : '#121212';
      g.strokeText(text, 0, 0);
      g.fillStyle = c0;
      g.fillText(text, 0, 0);
      const tw = Math.min(w - 24, g.measureText(text).width);
      for (let i = 0; i < 7; i++) {
        const dx = -tw / 2 + (((hash >> i) % 97) / 97) * tw;
        g.fillRect(dx, size * 0.25, 2.5, 6 + ((hash >> (i + 2)) % 18));
      }
    } else if (style === 'busStop') {
      if (!plate('busStop')) {
        g.fillStyle = '#efe9d6';
        g.fillRect(x, y, w, h);
        g.fillStyle = '#9b1c1c';
        g.fillRect(x, y, w, 34);
        g.fillStyle = '#f5efdc';
        g.font = `bold 20px ${SANS}`;
        g.fillText('BUS STOP', x + w / 2, y + 18);
        g.fillStyle = '#1a1410';
        g.font = `bold 11px ${SANS}`;
        g.fillText('KESSMOUTH CORP.', x + w / 2, y + 46);
        g.strokeStyle = '#1a1410';
        g.lineWidth = 3;
        g.strokeRect(x + 2, y + 2, w - 4, h - 4);
      }
      g.fillStyle = '#1c2a44';
      g.font = `bold 26px ${SANS}`;
      g.fillText(text, x + w / 2, y + 82);
    } else if (style === 'timetable') {
      if (!plate('timetable')) {
        g.fillStyle = '#e4dcc4';
        g.fillRect(x, y, w, h);
        g.fillStyle = '#9b1c1c';
        g.fillRect(x, y, w, 12);
        g.fillStyle = '#2a2420';
        for (let yy = y + 18; yy < y + h - 6; yy += 7) g.fillRect(x + 6, yy, w - 12 - ((yy * 7 + hash) % 14), 2);
      }
    } else if (style === 'aboard') {
      // newsagent's A-board: headline bill in a wire frame
      if (!plate('aboard')) {
        g.fillStyle = '#1a1a1a';
        g.fillRect(x, y, w, h);
        g.fillStyle = '#f2efe4';
        g.fillRect(x + 6, y + 6, w - 12, h - 12);
      }
      const [paper, head] = text.split(' · ');
      g.fillStyle = '#9b1c1c';
      g.font = `bold 12px ${SERIF}`;
      g.fillText((paper ?? '').toUpperCase(), x + w / 2, y + 18);
      g.fillStyle = '#111';
      const words = (head ?? text).toUpperCase().split(' ');
      const lines: string[] = [];
      let cur = '';
      for (const wd of words) {
        if ((cur + ' ' + wd).trim().length > 9 && cur) {
          lines.push(cur);
          cur = wd;
        } else cur = (cur + ' ' + wd).trim();
      }
      if (cur) lines.push(cur);
      g.font = `bold 17px ${SANS}`;
      lines.slice(0, 5).forEach((ln, i) => g.fillText(ln, x + w / 2, y + 42 + i * 19));
    } else if (style === 'plate') {
      if (!plate('plate')) {
        g.fillStyle = '#f1efe6';
        g.fillRect(x, y, w, h);
        g.strokeStyle = '#111';
        g.lineWidth = 3;
        g.strokeRect(x + 3, y + 3, w - 6, h - 6);
      }
      const size = fitFont(text, w - 28, h * 0.5, SANS);
      g.font = `bold ${size}px ${SANS}`;
      g.fillStyle = '#111';
      g.fillText(text, x + w / 2, y + h / 2 - 3);
      g.font = `bold 9px ${SANS}`;
      g.fillStyle = '#9b1c1c';
      g.fillText('LOW BOROUGH · K.1', x + w / 2, y + h - 10);
    } else if (style === 'painted') {
      const size = fitFont(text, w - 20, h * 0.72, SERIF);
      g.font = `bold ${size}px ${SERIF}`;
      g.fillStyle = 'rgba(232,222,196,0.9)';
      g.fillText(text, x + w / 2, y + h / 2);
    } else if (style === 'billboard') {
      const [brand, slogan] = text.split(' · ');
      const bg = ['#c8452c', '#1f3d5a', '#e0b23c', '#2b4d33', '#d9d0b8', '#3a1f2b'][hash % 6]!;
      const cell = plate(hash & 16 ? 'billboardB' : 'billboardA');
      if (cell) tint(cell, bg);
      else {
        g.fillStyle = bg;
        g.fillRect(x, y, w, h);
        g.strokeStyle = 'rgba(0,0,0,0.5)';
        g.lineWidth = 4;
        g.strokeRect(x + 8, y + 8, w - 16, h - 16);
      }
      // halftone dots in the corner, poster-print feel
      g.fillStyle = 'rgba(0,0,0,0.18)';
      for (let yy = 16; yy < h - 16; yy += 8) for (let xx = 0; xx < w * 0.4; xx += 8) g.fillRect(x + w - xx - 18, y + yy + 2, 3 * (1 - xx / (w * 0.4)), 3 * (1 - xx / (w * 0.4)));
      const light = bg === '#e0b23c' || bg === '#d9d0b8';
      const ink = light ? '#1a1612' : '#f3ead2';
      // the brand in a heavy serif or a grotesque by the campaign, the slogan in an italic under a rule
      const brandFam = hash & 4 ? SANS : SERIF;
      let size = fitFont(brand ?? text, w - 64, h * 0.3, brandFam);
      g.font = `bold ${size}px ${brandFam}`;
      g.fillStyle = 'rgba(0,0,0,0.25)';
      g.fillText(brand ?? text, x + w / 2 + 2, y + h * 0.4 + 3);
      g.fillStyle = ink;
      g.fillText(brand ?? text, x + w / 2, y + h * 0.4);
      g.fillRect(x + w * 0.3, y + h * 0.58, w * 0.4, 2);
      size = fitFont(slogan ?? '', w - 80, h * 0.12, SERIF, 'italic');
      g.font = `italic ${size}px ${SERIF}`;
      g.fillText(slogan ?? '', x + w / 2, y + h * 0.7);
    } else if (style === 'neon') {
      // The club blade: the name stacked down the sign in tube capitals, "club" in script under it when the
      // name does not say so itself. Tube colour by the name: pink, red, blue, amber.
      const [tubeC, coreC] = [
        ['#ff4f9a', '#fff1f7'],
        ['#ff3b2f', '#fff0ea'],
        ['#3fa9ff', '#eef7ff'],
        ['#ffb13b', '#fff8e8'],
      ][hash % 4]!;
      if (!plate('neon')) {
        g.fillStyle = '#140c12';
        g.fillRect(x, y, w, h);
      }
      const name = text.toUpperCase().replace(/^THE /, '');
      const script = !/CLUB/.test(name);
      const letters = name.split('');
      const top = y + 18;
      const bottom = y + h - (script ? 74 : 18);
      const step = Math.min(46, (bottom - top) / letters.length);
      const size = Math.min(step * 0.86, 40);
      g.font = `bold ${size}px ${SANS}`;
      tube(() => letters.forEach((ch, i) => g.strokeText(ch, x + w / 2, top + step * (i + 0.5))), tubeC!, coreC!, Math.max(3, size * 0.14));
      if (script) {
        const sc2 = fitFont('club', w - 24, 44, SCRIPT, 'italic');
        g.font = `italic ${sc2}px ${SCRIPT}`;
        tube(() => g.strokeText('club', x + w / 2, y + h - 38), hash % 4 === 2 ? '#ff4f9a' : '#3fa9ff', '#f4fbff', 3);
      }
    } else if (style === 'police') {
      if (!plate('police')) {
        g.fillStyle = '#1b2f6b';
        g.fillRect(x, y, w, h);
      }
      const size = fitFont(text, w - 36, h * 0.55, SANS);
      g.font = `bold ${size}px ${SANS}`;
      g.fillStyle = '#f2f2f2';
      g.fillText(text, x + w / 2, y + h / 2 + 2);
    } else if (style === 'hanging') {
      const cell = plate(['hangingA', 'hangingB', 'hangingC'][(hash >>> 5) % 3]!);
      if (cell) {
        // the name in gilt on the band under the emblem
        const [ix = 8, iy = h - 30, iw = w - 16, ih = 20] = cell.inner ?? [];
        const name = text.toUpperCase().replace(/^THE /, '');
        const size = fitFont(name, iw - 6, ih * 0.8, SERIF);
        gilt(name, x + ix + iw / 2, y + iy + ih / 2 + 1, size, SERIF, '#000');
      } else {
        g.fillStyle = '#1b1712';
        g.fillRect(x, y, w, h);
        g.fillStyle = '#c9a24a';
        g.fillRect(x + 4, y + 4, w - 8, h - 8);
        g.fillStyle = '#2b3b52';
        g.fillRect(x + 8, y + 8, w - 16, h - 16);
        // emblem: an anchor
        g.strokeStyle = '#e8d6a0';
        g.fillStyle = '#e8d6a0';
        g.lineWidth = 5;
        const cx = x + w / 2;
        const cy = y + h / 2;
        g.beginPath();
        g.moveTo(cx, cy - 34);
        g.lineTo(cx, cy + 30);
        g.moveTo(cx - 18, cy - 20);
        g.lineTo(cx + 18, cy - 20);
        g.stroke();
        g.beginPath();
        g.arc(cx, cy + 6, 24, 0.15 * Math.PI, 0.85 * Math.PI);
        g.stroke();
        g.beginPath();
        g.arc(cx, cy - 40, 6, 0, Math.PI * 2);
        g.stroke();
      }
    }
    g.restore();
  };

  // With the baked plates, a sign is painted once they are in (or have failed), not before and again after:
  // the city's ~600 signs were painted twice at boot. The loading screen waits for the plates.
  let deferred = false;
  if (SIGN_PLATES && typeof Image !== 'undefined') {
    deferred = true;
    const img = new Image();
    needForFirstView(
      new Promise<void>((resolve) => {
        const paintAll = (): void => {
          deferred = false;
          for (const en of entries) paint(en);
          if (entries.length) texture.needsUpdate = true;
          resolve();
        };
        img.onload = () => {
          plates = img;
          paintAll();
        };
        // no plates: the painted fallback
        img.onerror = paintAll;
      }),
    );
    img.src = `${BASE}textures/sign-plates.webp`;
  }

  return {
    texture,
    add(text, style, seed = 0) {
      const key = `${style}|${text}|${seed}`;
      const hit = cache.get(key);
      if (hit) return hit;
      const [w, h] = dims[style];
      const pos = alloc(w, h);
      if (!pos) return { uv: [0, 0, 0.001, 0.001], aspect: w / h };
      const [x, y] = pos;
      const en: Entry = { x, y, w, h, text, style, seed };
      entries.push(en);
      if (!deferred) {
        paint(en);
        texture.needsUpdate = true;
      }
      const out = { uv: [x / W, 1 - (y + h) / H, (x + w) / W, 1 - y / H] as [number, number, number, number], aspect: w / h };
      cache.set(key, out);
      return out;
    },
  };
}
